import { useState, useEffect, useCallback } from 'react';
import { useAdminAuth, useAdminData } from '../../hooks/useAdmin';
import { AdminLogin } from './AdminLogin';
import { 
  LayoutDashboard, Calendar, Image, Heart, LogOut, Plus, 
  Pencil, Trash2, Save, X, Loader2, Menu, Home, Settings, RefreshCw, Phone, Users, Key, FileText, Upload, FileEdit, ImageIcon, Copy, Check, Navigation, GripVertical, ChevronUp, ChevronDown, Mail, Eye, Download, UploadCloud, Database, AlertTriangle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export const AdminDashboard = () => {
  const { isAuthenticated, isLoading, login, logout, getAuthHeader, userInfo, updateCredentials } = useAdminAuth();
  const [activeTab, setActiveTab] = useState('homepage');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-secondary" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <AdminLogin onLogin={login} />;
  }

  const canManageUsers = userInfo?.canManageUsers || false;

  const tabs = [
    { id: 'homepage', label: 'Homepage', icon: Settings },
    { id: 'navigation', label: 'Navigation', icon: Navigation },
    { id: 'contact', label: 'Contact Page', icon: Phone },
    { id: 'messages', label: 'Messages', icon: Mail },
    { id: 'pages', label: 'Pages', icon: FileEdit },
    { id: 'events', label: 'Events', icon: Calendar },
    { id: 'ministries', label: 'Ministries', icon: Heart },
    { id: 'gallery', label: 'Gallery', icon: Image },
    { id: 'images', label: 'Images', icon: ImageIcon },
    { id: 'documents', label: 'Documents', icon: FileText },
    ...(canManageUsers ? [{ id: 'users', label: 'Users', icon: Users }] : []),
    ...(canManageUsers ? [{ id: 'backup', label: 'Backup', icon: Database }] : []),
  ];

  return (
    <div className="min-h-screen bg-slate-100 flex">
      {/* Sidebar */}
      <aside className={`${sidebarOpen ? 'w-64' : 'w-20'} bg-primary-900 text-white transition-all duration-300 flex flex-col`}>
        <div className="p-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-secondary rounded-lg flex items-center justify-center flex-shrink-0">
              <LayoutDashboard className="w-5 h-5" />
            </div>
            {sidebarOpen && (
              <div>
                <p className="font-semibold">Admin Panel</p>
                <p className="text-xs text-slate-400">Geneva UMC</p>
              </div>
            )}
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                activeTab === tab.id 
                  ? 'bg-secondary text-white' 
                  : 'text-slate-300 hover:bg-white/10'
              }`}
              data-testid={`admin-tab-${tab.id}`}
            >
              <tab.icon className="w-5 h-5 flex-shrink-0" />
              {sidebarOpen && <span>{tab.label}</span>}
            </button>
          ))}
        </nav>

        <div className="p-4 border-t border-white/10 space-y-2">
          <a
            href="/"
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-white/10 transition-colors"
          >
            <Home className="w-5 h-5 flex-shrink-0" />
            {sidebarOpen && <span>View Site</span>}
          </a>
          <button
            onClick={logout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-red-600/20 hover:text-red-400 transition-colors"
            data-testid="admin-logout"
          >
            <LogOut className="w-5 h-5 flex-shrink-0" />
            {sidebarOpen && <span>Logout</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        <header className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 hover:bg-slate-100 rounded-lg"
            >
              <Menu className="w-5 h-5" />
            </button>
            <h1 className="font-heading text-xl font-semibold text-primary-900 capitalize">
              Manage {activeTab}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-500">
              Logged in as <span className="font-medium text-primary-900">{userInfo?.username}</span>
            </span>
            <button
              onClick={() => setShowPasswordModal(true)}
              className="flex items-center gap-2 px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              data-testid="change-password-btn"
            >
              <Key className="w-4 h-4" />
              Change Password
            </button>
          </div>
        </header>

        <div className="p-6">
          {activeTab === 'homepage' && <HomepageManager authHeader={getAuthHeader()} />}
          {activeTab === 'navigation' && <NavigationManager authHeader={getAuthHeader()} />}
          {activeTab === 'contact' && <ContactManager authHeader={getAuthHeader()} />}
          {activeTab === 'messages' && <MessagesManager authHeader={getAuthHeader()} />}
          {activeTab === 'pages' && <PagesManager authHeader={getAuthHeader()} />}
          {activeTab === 'events' && <EventsManager authHeader={getAuthHeader()} />}
          {activeTab === 'ministries' && <MinistriesManager authHeader={getAuthHeader()} />}
          {activeTab === 'gallery' && <GalleryManager authHeader={getAuthHeader()} />}
          {activeTab === 'images' && <ImagesManager authHeader={getAuthHeader()} />}
          {activeTab === 'documents' && <DocumentsManager authHeader={getAuthHeader()} />}
          {activeTab === 'users' && canManageUsers && <UsersManager authHeader={getAuthHeader()} currentUsername={userInfo?.username} />}
          {activeTab === 'backup' && canManageUsers && <BackupManager authHeader={getAuthHeader()} />}
        </div>
      </main>

      {/* Change Password Modal */}
      {showPasswordModal && (
        <ChangePasswordModal 
          authHeader={getAuthHeader()} 
          onClose={() => setShowPasswordModal(false)}
          onSuccess={(newPassword) => {
            updateCredentials(newPassword);
            setShowPasswordModal(false);
          }}
        />
      )}
    </div>
  );
};

// Change Password Modal Component
const ChangePasswordModal = ({ authHeader, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate
    if (!formData.currentPassword || !formData.newPassword || !formData.confirmPassword) {
      setError('All fields are required');
      return;
    }

    if (formData.newPassword.length < 6) {
      setError('New password must be at least 6 characters');
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/users/me/password`, {
        method: 'PUT',
        headers: {
          ...authHeader,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          currentPassword: formData.currentPassword,
          newPassword: formData.newPassword
        })
      });

      if (response.ok) {
        alert('Password changed successfully!');
        onSuccess(formData.newPassword);
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to change password');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <h2 className="font-heading text-lg font-semibold text-primary-900">Change Password</h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Current Password</label>
            <input
              type="password"
              value={formData.currentPassword}
              onChange={(e) => setFormData({...formData, currentPassword: e.target.value})}
              className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-secondary"
              placeholder="Enter current password"
              data-testid="current-password-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">New Password</label>
            <input
              type="password"
              value={formData.newPassword}
              onChange={(e) => setFormData({...formData, newPassword: e.target.value})}
              className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-secondary"
              placeholder="Enter new password (min 6 characters)"
              data-testid="new-password-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Confirm New Password</label>
            <input
              type="password"
              value={formData.confirmPassword}
              onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
              className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-secondary"
              placeholder="Confirm new password"
              data-testid="confirm-password-input"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 flex items-center justify-center gap-2 bg-secondary text-white px-4 py-2 rounded-lg hover:bg-secondary-600 disabled:opacity-50"
              data-testid="submit-password-change"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Key className="w-4 h-4" />}
              Change Password
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};


// Navigation Manager Component
const NavigationManager = ({ authHeader }) => {
  const [navLinks, setNavLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const fetchNavigation = useCallback(async () => {
    if (!authHeader?.Authorization) return;
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/navigation`, { headers: authHeader });
      if (response.ok) {
        const data = await response.json();
        setNavLinks(data.links || []);
      }
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to fetch navigation:', err);
      }
    } finally {
      setLoading(false);
    }
  }, [authHeader]);

  useEffect(() => {
    fetchNavigation();
  }, [fetchNavigation]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/navigation`, {
        method: 'PUT',
        headers: {
          ...authHeader,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ links: navLinks })
      });

      if (response.ok) {
        alert('Navigation saved successfully!');
        setHasChanges(false);
      } else {
        alert('Failed to save navigation');
      }
    } catch (err) {
      alert('Error saving navigation');
    } finally {
      setSaving(false);
    }
  };

  const updateLinkName = (linkIndex, newName) => {
    const updated = [...navLinks];
    updated[linkIndex] = { ...updated[linkIndex], name: newName };
    setNavLinks(updated);
    setHasChanges(true);
  };

  const updateLinkPath = (linkIndex, newPath) => {
    const updated = [...navLinks];
    updated[linkIndex] = { ...updated[linkIndex], path: newPath };
    setNavLinks(updated);
    setHasChanges(true);
  };

  const addSubLink = (linkIndex) => {
    const updated = [...navLinks];
    const dropdown = updated[linkIndex].dropdown || [];
    dropdown.push({ name: 'New Link', path: '/' });
    updated[linkIndex] = { ...updated[linkIndex], dropdown };
    setNavLinks(updated);
    setHasChanges(true);
  };

  const updateSubLinkName = (linkIndex, subIndex, newName) => {
    const updated = [...navLinks];
    updated[linkIndex].dropdown[subIndex] = { 
      ...updated[linkIndex].dropdown[subIndex], 
      name: newName 
    };
    setNavLinks(updated);
    setHasChanges(true);
  };

  const updateSubLinkPath = (linkIndex, subIndex, newPath) => {
    const updated = [...navLinks];
    updated[linkIndex].dropdown[subIndex] = { 
      ...updated[linkIndex].dropdown[subIndex], 
      path: newPath 
    };
    setNavLinks(updated);
    setHasChanges(true);
  };

  const deleteSubLink = (linkIndex, subIndex) => {
    const updated = [...navLinks];
    updated[linkIndex].dropdown.splice(subIndex, 1);
    setNavLinks(updated);
    setHasChanges(true);
  };

  const moveSubLink = (linkIndex, subIndex, direction) => {
    const updated = [...navLinks];
    const dropdown = updated[linkIndex].dropdown;
    const newIndex = direction === 'up' ? subIndex - 1 : subIndex + 1;
    
    if (newIndex < 0 || newIndex >= dropdown.length) return;
    
    const temp = dropdown[subIndex];
    dropdown[subIndex] = dropdown[newIndex];
    dropdown[newIndex] = temp;
    
    updated[linkIndex].dropdown = dropdown;
    setNavLinks(updated);
    setHasChanges(true);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-secondary" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <p className="text-slate-600">Edit navigation menu items and sub-links</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving || !hasChanges}
          className="flex items-center gap-2 bg-secondary text-white px-4 py-2 rounded-lg hover:bg-secondary-600 disabled:opacity-50 transition-colors"
          data-testid="save-navigation-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save Navigation
        </button>
      </div>

      {hasChanges && (
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded-lg mb-6 flex items-center gap-2">
          <span className="text-yellow-500">!</span>
          You have unsaved changes. Click "Save Navigation" to apply them.
        </div>
      )}

      <div className="space-y-4">
        {navLinks.map((link, linkIndex) => (
          <div 
            key={link.id || linkIndex}
            className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden"
          >
            {/* Main Link Header */}
            <div className="p-5 border-b border-slate-100">
              <div className="flex items-center gap-4">
                <div className="p-2 bg-slate-100 rounded-lg">
                  <GripVertical className="w-4 h-4 text-slate-400" />
                </div>
                <div className="flex-1 grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Menu Label</label>
                    <input
                      type="text"
                      value={link.name}
                      onChange={(e) => updateLinkName(linkIndex, e.target.value)}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-secondary text-sm"
                      data-testid={`nav-link-name-${linkIndex}`}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">URL Path</label>
                    <input
                      type="text"
                      value={link.path}
                      onChange={(e) => updateLinkPath(linkIndex, e.target.value)}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-secondary text-sm"
                      data-testid={`nav-link-path-${linkIndex}`}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Sub-links Section */}
            <div className="bg-slate-50 p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-slate-600">
                  Dropdown Links ({link.dropdown?.length || 0})
                </span>
                <button
                  onClick={() => addSubLink(linkIndex)}
                  className="flex items-center gap-1 text-sm text-secondary hover:text-secondary-600 transition-colors"
                  data-testid={`add-sublink-${linkIndex}`}
                >
                  <Plus className="w-4 h-4" />
                  Add Sub-link
                </button>
              </div>

              {link.dropdown && link.dropdown.length > 0 ? (
                <div className="space-y-2">
                  {link.dropdown.map((subLink, subIndex) => (
                    <div 
                      key={`sublink-${subIndex}`}
                      className="flex items-center gap-3 bg-white p-3 rounded-lg border border-slate-200"
                    >
                      <div className="flex flex-col gap-1">
                        <button
                          onClick={() => moveSubLink(linkIndex, subIndex, 'up')}
                          disabled={subIndex === 0}
                          className="p-1 text-slate-400 hover:text-slate-600 disabled:opacity-30"
                          title="Move up"
                        >
                          <ChevronUp className="w-3 h-3" />
                        </button>
                        <button
                          onClick={() => moveSubLink(linkIndex, subIndex, 'down')}
                          disabled={subIndex === link.dropdown.length - 1}
                          className="p-1 text-slate-400 hover:text-slate-600 disabled:opacity-30"
                          title="Move down"
                        >
                          <ChevronDown className="w-3 h-3" />
                        </button>
                      </div>
                      <div className="flex-1 grid grid-cols-2 gap-3">
                        <input
                          type="text"
                          value={subLink.name}
                          onChange={(e) => updateSubLinkName(linkIndex, subIndex, e.target.value)}
                          className="px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-secondary text-sm"
                          placeholder="Link text"
                          data-testid={`sublink-name-${linkIndex}-${subIndex}`}
                        />
                        <input
                          type="text"
                          value={subLink.path}
                          onChange={(e) => updateSubLinkPath(linkIndex, subIndex, e.target.value)}
                          className="px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-secondary text-sm"
                          placeholder="/path"
                          data-testid={`sublink-path-${linkIndex}-${subIndex}`}
                        />
                      </div>
                      <button
                        onClick={() => deleteSubLink(linkIndex, subIndex)}
                        className="p-2 text-slate-400 hover:text-red-500 transition-colors"
                        title="Delete sub-link"
                        data-testid={`delete-sublink-${linkIndex}-${subIndex}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-400 italic">No dropdown links. Click "Add Sub-link" to create one.</p>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Help Text */}
      <div className="mt-8 bg-slate-50 rounded-xl p-6">
        <h4 className="font-heading font-semibold text-primary-900 mb-2">Navigation Tips</h4>
        <ul className="text-sm text-slate-600 list-disc list-inside space-y-1">
          <li>Edit menu labels and URL paths for each navigation item</li>
          <li>Add dropdown sub-links to create nested menus</li>
          <li>Use the arrows to reorder sub-links within a dropdown</li>
          <li>Click "Save Navigation" to publish your changes to the live site</li>
          <li>Internal links should start with "/" (e.g., /about, /contact)</li>
          <li>External links should include the full URL (e.g., https://example.com)</li>
        </ul>
      </div>
    </div>
  );
};


// Messages Manager Component (Contact Form Submissions)
const MessagesManager = ({ authHeader }) => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);

  const fetchSubmissions = useCallback(async () => {
    if (!authHeader?.Authorization) return;
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/contact-submissions`, { headers: authHeader });
      if (response.ok) {
        const data = await response.json();
        setSubmissions(data);
      }
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to fetch submissions:', err);
      }
    } finally {
      setLoading(false);
    }
  }, [authHeader]);

  useEffect(() => {
    fetchSubmissions();
  }, [fetchSubmissions]);

  const markAsRead = async (id) => {
    try {
      await fetch(`${API_URL}/api/admin/contact-submissions/${id}/read`, {
        method: 'PUT',
        headers: authHeader
      });
      setSubmissions(submissions.map(s => s.id === id ? { ...s, isRead: true } : s));
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to mark as read:', err);
      }
    }
  };

  const deleteSubmission = async (id) => {
    if (!window.confirm('Are you sure you want to delete this message?')) return;
    try {
      await fetch(`${API_URL}/api/admin/contact-submissions/${id}`, {
        method: 'DELETE',
        headers: authHeader
      });
      setSubmissions(submissions.filter(s => s.id !== id));
      if (selectedMessage?.id === id) setSelectedMessage(null);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to delete submission:', err);
      }
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const unreadCount = submissions.filter(s => !s.isRead).length;

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-secondary" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <p className="text-slate-600">
            {submissions.length} message{submissions.length !== 1 ? 's' : ''}
            {unreadCount > 0 && <span className="ml-2 text-secondary font-semibold">({unreadCount} unread)</span>}
          </p>
        </div>
        <button
          onClick={fetchSubmissions}
          className="flex items-center gap-2 text-slate-600 hover:text-secondary transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {submissions.length === 0 ? (
        <div className="text-center py-12 bg-slate-50 rounded-xl">
          <Mail className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">No messages yet</p>
          <p className="text-slate-400 text-sm">Contact form submissions will appear here</p>
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Message List */}
          <div className="space-y-3">
            {submissions.map((submission) => (
              <div
                key={submission.id}
                onClick={() => {
                  setSelectedMessage(submission);
                  if (!submission.isRead) markAsRead(submission.id);
                }}
                className={`p-4 rounded-xl border cursor-pointer transition-all ${
                  selectedMessage?.id === submission.id
                    ? 'border-secondary bg-secondary/5'
                    : submission.isRead
                    ? 'border-slate-200 bg-white hover:border-slate-300'
                    : 'border-secondary/30 bg-secondary/5 hover:border-secondary'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      {!submission.isRead && (
                        <span className="w-2 h-2 bg-secondary rounded-full flex-shrink-0" />
                      )}
                      <p className={`font-medium truncate ${!submission.isRead ? 'text-primary-900' : 'text-slate-700'}`}>
                        {submission.name}
                      </p>
                    </div>
                    <p className="text-sm text-slate-500 truncate">{submission.subject || 'No subject'}</p>
                    <p className="text-xs text-slate-400 mt-1">{formatDate(submission.submittedAt)}</p>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteSubmission(submission.id);
                    }}
                    className="p-1 text-slate-400 hover:text-red-500 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Message Detail */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            {selectedMessage ? (
              <div>
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="font-heading text-lg font-semibold text-primary-900">
                      {selectedMessage.subject || 'No subject'}
                    </h3>
                    <p className="text-sm text-slate-500">{formatDate(selectedMessage.submittedAt)}</p>
                  </div>
                </div>
                
                <div className="space-y-3 mb-6">
                  <div className="flex items-center gap-3 text-sm">
                    <span className="text-slate-500 w-16">From:</span>
                    <span className="font-medium text-primary-900">{selectedMessage.name}</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <span className="text-slate-500 w-16">Email:</span>
                    <a href={`mailto:${selectedMessage.email}`} className="text-secondary hover:underline">
                      {selectedMessage.email}
                    </a>
                  </div>
                  {selectedMessage.phone && (
                    <div className="flex items-center gap-3 text-sm">
                      <span className="text-slate-500 w-16">Phone:</span>
                      <a href={`tel:${selectedMessage.phone}`} className="text-secondary hover:underline">
                        {selectedMessage.phone}
                      </a>
                    </div>
                  )}
                </div>

                <div className="border-t border-slate-100 pt-4">
                  <p className="text-sm text-slate-500 mb-2">Message:</p>
                  <p className="text-slate-700 whitespace-pre-wrap">{selectedMessage.message}</p>
                </div>

                <div className="mt-6 flex gap-3">
                  <a
                    href={`mailto:${selectedMessage.email}?subject=Re: ${selectedMessage.subject || 'Your message'}`}
                    className="flex items-center gap-2 bg-secondary text-white px-4 py-2 rounded-lg hover:bg-secondary-600 transition-colors"
                  >
                    <Mail className="w-4 h-4" />
                    Reply
                  </a>
                  <button
                    onClick={() => deleteSubmission(selectedMessage.id)}
                    className="flex items-center gap-2 text-red-600 hover:text-red-700 px-4 py-2 rounded-lg border border-red-200 hover:bg-red-50 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <Eye className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">Select a message to view</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};



// Events Manager Component
const EventsManager = ({ authHeader }) => {
  const { data, loading, refetch, deleteItem } = useAdminData('events', authHeader);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: '', description: '', eventDate: '', startTime: '', endTime: '', 
    location: '', featured: false, recurrence: 'none', sermonTitle: '', sermonScripture: ''
  });

  const defaultFormData = {
    title: '', description: '', eventDate: '', startTime: '', endTime: '', 
    location: '', featured: false, recurrence: 'none', sermonTitle: '', sermonScripture: ''
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editingId) {
        // Update existing event
        const response = await fetch(`${API_URL}/api/admin/events/${editingId}`, {
          method: 'PUT',
          headers: { ...authHeader, 'Content-Type': 'application/json' },
          body: JSON.stringify(formData)
        });
        if (!response.ok) throw new Error('Failed to update event');
      } else {
        // Create new event(s) - may create multiple if recurring
        const response = await fetch(`${API_URL}/api/admin/events`, {
          method: 'POST',
          headers: { ...authHeader, 'Content-Type': 'application/json' },
          body: JSON.stringify(formData)
        });
        if (!response.ok) throw new Error('Failed to create event');
        const created = await response.json();
        if (created.length > 1) {
          alert(`Created ${created.length} recurring events!`);
        }
      }
      await refetch();
      setShowForm(false);
      setEditingId(null);
      setFormData(defaultFormData);
    } catch (err) {
      alert('Error saving event: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (item) => {
    setFormData({
      ...defaultFormData,
      ...item,
      recurrence: 'none' // Always reset recurrence when editing
    });
    setEditingId(item.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this event?')) {
      await deleteItem(id);
    }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-secondary" /></div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <p className="text-slate-600">{data.length} events</p>
        <button
          onClick={() => { setShowForm(true); setEditingId(null); setFormData(defaultFormData); }}
          className="flex items-center gap-2 bg-secondary text-white px-4 py-2 rounded-lg hover:bg-secondary-600 transition-colors"
          data-testid="add-event-btn"
        >
          <Plus className="w-5 h-5" />
          Add Event
        </button>
      </div>

      {showForm && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
          <h3 className="font-heading text-lg font-semibold mb-4">{editingId ? 'Edit Event' : 'New Event'}</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <input
              type="text"
              placeholder="Event Title *"
              value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              className="px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-secondary"
              data-testid="event-title-input"
            />
            <input
              type="date"
              value={formData.eventDate}
              onChange={(e) => setFormData({...formData, eventDate: e.target.value})}
              className="px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-secondary"
              data-testid="event-date-input"
            />
            <input
              type="text"
              placeholder="Start Time (e.g., 9:30 AM)"
              value={formData.startTime}
              onChange={(e) => setFormData({...formData, startTime: e.target.value})}
              className="px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-secondary"
            />
            <input
              type="text"
              placeholder="End Time (e.g., 10:30 AM)"
              value={formData.endTime}
              onChange={(e) => setFormData({...formData, endTime: e.target.value})}
              className="px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-secondary"
            />
            <input
              type="text"
              placeholder="Location"
              value={formData.location}
              onChange={(e) => setFormData({...formData, location: e.target.value})}
              className="px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-secondary"
            />
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.featured}
                  onChange={(e) => setFormData({...formData, featured: e.target.checked})}
                  className="w-5 h-5 text-secondary"
                />
                Featured Event
              </label>
            </div>
          </div>

          {/* Recurrence - only show for new events */}
          {!editingId && (
            <div className="mt-4 p-4 bg-slate-50 rounded-lg">
              <label className="block text-sm font-medium text-slate-700 mb-2">Repeat Event</label>
              <select
                value={formData.recurrence}
                onChange={(e) => setFormData({...formData, recurrence: e.target.value})}
                className="px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-secondary bg-white"
                data-testid="event-recurrence-select"
              >
                <option value="none">No Repeat (Single Event)</option>
                <option value="weekly">Weekly (Creates 4 weeks)</option>
                <option value="monthly">Monthly (Creates 2 months)</option>
              </select>
              {formData.recurrence !== 'none' && (
                <p className="text-sm text-slate-500 mt-2">
                  {formData.recurrence === 'weekly' 
                    ? '4 events will be created, one for each week starting from the selected date.'
                    : '2 events will be created, one for each month starting from the selected date.'}
                  <br />
                  <span className="text-amber-600">Note: Sermon info will only be added to the first event.</span>
                </p>
              )}
            </div>
          )}

          <textarea
            placeholder="Description"
            value={formData.description}
            onChange={(e) => setFormData({...formData, description: e.target.value})}
            className="w-full mt-4 px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-secondary"
            rows={3}
          />

          {/* Sermon Info Section */}
          <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-100">
            <h4 className="text-sm font-medium text-blue-900 mb-3">Sermon Information (Optional)</h4>
            <div className="grid md:grid-cols-2 gap-4">
              <input
                type="text"
                placeholder="Sermon Title (e.g., 'Doubting Thomas')"
                value={formData.sermonTitle || ''}
                onChange={(e) => setFormData({...formData, sermonTitle: e.target.value})}
                className="px-4 py-2 border border-blue-200 rounded-lg focus:outline-none focus:border-secondary bg-white"
                data-testid="event-sermon-title"
              />
              <input
                type="text"
                placeholder="Scripture (e.g., 'John 20:19-31')"
                value={formData.sermonScripture || ''}
                onChange={(e) => setFormData({...formData, sermonScripture: e.target.value})}
                className="px-4 py-2 border border-blue-200 rounded-lg focus:outline-none focus:border-secondary bg-white"
                data-testid="event-sermon-scripture"
              />
            </div>
          </div>

          <div className="flex gap-3 mt-4">
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 bg-secondary text-white px-4 py-2 rounded-lg hover:bg-secondary-600 disabled:opacity-50"
              data-testid="save-event-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button
              onClick={() => { setShowForm(false); setEditingId(null); }}
              className="flex items-center gap-2 bg-slate-200 text-slate-700 px-4 py-2 rounded-lg hover:bg-slate-300"
            >
              <X className="w-4 h-4" />
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-6 py-3 text-sm font-semibold text-slate-600">Title</th>
              <th className="text-left px-6 py-3 text-sm font-semibold text-slate-600">Date</th>
              <th className="text-left px-6 py-3 text-sm font-semibold text-slate-600">Time</th>
              <th className="text-left px-6 py-3 text-sm font-semibold text-slate-600">Sermon</th>
              <th className="text-right px-6 py-3 text-sm font-semibold text-slate-600">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data.map((event) => (
              <tr key={event.id} className="hover:bg-slate-50">
                <td className="px-6 py-4">
                  <span className="font-medium text-primary-900">{event.title}</span>
                  {event.featured && <span className="ml-2 text-xs bg-secondary/10 text-secondary px-2 py-0.5 rounded">Featured</span>}
                  {event.location && <p className="text-xs text-slate-500">{event.location}</p>}
                </td>
                <td className="px-6 py-4 text-slate-600">{event.eventDate}</td>
                <td className="px-6 py-4 text-slate-600">{event.startTime}{event.endTime ? ` - ${event.endTime}` : ''}</td>
                <td className="px-6 py-4 text-slate-600">
                  {event.sermonTitle || event.sermonScripture ? (
                    <div className="text-sm">
                      {event.sermonTitle && <p className="font-medium">{event.sermonTitle}</p>}
                      {event.sermonScripture && <p className="text-xs text-blue-600">{event.sermonScripture}</p>}
                    </div>
                  ) : (
                    <span className="text-slate-400 text-sm">—</span>
                  )}
                </td>
                <td className="px-6 py-4 text-right">
                  <button onClick={() => handleEdit(event)} className="p-2 text-slate-500 hover:text-secondary"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => handleDelete(event.id)} className="p-2 text-slate-500 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {data.length === 0 && (
          <div className="text-center py-12 text-slate-500">No events yet. Add your first event!</div>
        )}
      </div>
    </div>
  );
};

// Ministries Manager Component
const MinistriesManager = ({ authHeader }) => {
  const { data, loading, createItem, updateItem, deleteItem } = useAdminData('ministries', authHeader);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    title: '', description: '', imageUrl: '', link: '/ministries', sortOrder: 0, showOnHomepage: true
  });

  const handleSave = async () => {
    if (editingId) {
      await updateItem(editingId, formData);
    } else {
      await createItem(formData);
    }
    setShowForm(false);
    setEditingId(null);
    setFormData({ title: '', description: '', imageUrl: '', link: '/ministries', sortOrder: 0, showOnHomepage: true });
  };

  const handleEdit = (item) => {
    setFormData(item);
    setEditingId(item.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this ministry?')) {
      await deleteItem(id);
    }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-secondary" /></div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <p className="text-slate-600">{data.length} ministries</p>
        <button
          onClick={() => { setShowForm(true); setEditingId(null); setFormData({ title: '', description: '', imageUrl: '', link: '/ministries', sortOrder: 0, showOnHomepage: true }); }}
          className="flex items-center gap-2 bg-secondary text-white px-4 py-2 rounded-lg hover:bg-secondary-600 transition-colors"
          data-testid="add-ministry-btn"
        >
          <Plus className="w-5 h-5" />
          Add Ministry
        </button>
      </div>

      {showForm && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
          <h3 className="font-heading text-lg font-semibold mb-4">{editingId ? 'Edit Ministry' : 'New Ministry'}</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <input
              type="text"
              placeholder="Ministry Title *"
              value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              className="px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-secondary"
              data-testid="ministry-title-input"
            />
            <input
              type="text"
              placeholder="Image URL"
              value={formData.imageUrl}
              onChange={(e) => setFormData({...formData, imageUrl: e.target.value})}
              className="px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-secondary"
            />
            <input
              type="text"
              placeholder="Link (e.g., /ministries)"
              value={formData.link}
              onChange={(e) => setFormData({...formData, link: e.target.value})}
              className="px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-secondary"
            />
            <input
              type="number"
              placeholder="Sort Order"
              value={formData.sortOrder}
              onChange={(e) => setFormData({...formData, sortOrder: parseInt(e.target.value) || 0})}
              className="px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-secondary"
            />
          </div>
          <textarea
            placeholder="Description *"
            value={formData.description}
            onChange={(e) => setFormData({...formData, description: e.target.value})}
            className="w-full mt-4 px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-secondary"
            rows={3}
            data-testid="ministry-description-input"
          />
          <label className="flex items-center gap-2 mt-4">
            <input
              type="checkbox"
              checked={formData.showOnHomepage}
              onChange={(e) => setFormData({...formData, showOnHomepage: e.target.checked})}
              className="w-5 h-5 text-secondary"
            />
            Show on Homepage
          </label>
          <div className="flex gap-3 mt-4">
            <button onClick={handleSave} className="flex items-center gap-2 bg-secondary text-white px-4 py-2 rounded-lg hover:bg-secondary-600" data-testid="save-ministry-btn">
              <Save className="w-4 h-4" /> Save
            </button>
            <button onClick={() => { setShowForm(false); setEditingId(null); }} className="flex items-center gap-2 bg-slate-200 text-slate-700 px-4 py-2 rounded-lg hover:bg-slate-300">
              <X className="w-4 h-4" /> Cancel
            </button>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {data.map((ministry) => (
          <div key={ministry.id} className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
            {ministry.imageUrl && (
              <img src={ministry.imageUrl} alt={ministry.title} className="w-full h-40 object-cover" />
            )}
            <div className="p-4">
              <h3 className="font-heading font-semibold text-primary-900 mb-2">{ministry.title}</h3>
              <p className="text-sm text-slate-600 line-clamp-2 mb-4">{ministry.description}</p>
              <div className="flex justify-between items-center">
                <span className={`text-xs px-2 py-1 rounded ${ministry.showOnHomepage ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                  {ministry.showOnHomepage ? 'On Homepage' : 'Hidden'}
                </span>
                <div>
                  <button onClick={() => handleEdit(ministry)} className="p-2 text-slate-500 hover:text-secondary"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => handleDelete(ministry.id)} className="p-2 text-slate-500 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      {data.length === 0 && (
        <div className="text-center py-12 text-slate-500 bg-white rounded-xl">No ministries yet. Add your first ministry!</div>
      )}
    </div>
  );
};

// Gallery Manager Component  
const GalleryManager = ({ authHeader }) => {
  const { data, loading, createItem, updateItem, deleteItem } = useAdminData('gallery', authHeader);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    title: '', caption: '', imageUrl: '', category: 'Other', sortOrder: 0
  });

  const categories = ['Worship', 'Community', 'Youth', 'Events', 'Building', 'Daycare and Preschool', 'Other'];

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const uploadData = new FormData();
      uploadData.append('file', file);

      const response = await fetch(`${API_URL}/api/admin/upload/image`, {
        method: 'POST',
        headers: { Authorization: authHeader.Authorization },
        body: uploadData
      });

      if (response.ok) {
        const result = await response.json();
        setFormData({...formData, imageUrl: `${API_URL}${result.url}`});
      } else {
        const error = await response.json();
        alert(error.detail || 'Upload failed');
      }
    } catch (err) {
      alert('Upload failed: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (editingId) {
      await updateItem(editingId, formData);
    } else {
      await createItem(formData);
    }
    setShowForm(false);
    setEditingId(null);
    setFormData({ title: '', caption: '', imageUrl: '', category: 'Other', sortOrder: 0 });
  };

  const handleEdit = (item) => {
    setFormData(item);
    setEditingId(item.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this image?')) {
      await deleteItem(id);
    }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-secondary" /></div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <p className="text-slate-600">{data.length} images</p>
        <button
          onClick={() => { setShowForm(true); setEditingId(null); setFormData({ title: '', caption: '', imageUrl: '', category: 'Other', sortOrder: 0 }); }}
          className="flex items-center gap-2 bg-secondary text-white px-4 py-2 rounded-lg hover:bg-secondary-600 transition-colors"
          data-testid="add-gallery-btn"
        >
          <Plus className="w-5 h-5" />
          Add Image
        </button>
      </div>

      {showForm && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
          <h3 className="font-heading text-lg font-semibold mb-4">{editingId ? 'Edit Image' : 'New Image'}</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <input
              type="text"
              placeholder="Title *"
              value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              className="px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-secondary"
              data-testid="gallery-title-input"
            />
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Image URL *"
                value={formData.imageUrl}
                onChange={(e) => setFormData({...formData, imageUrl: e.target.value})}
                className="flex-1 px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-secondary"
                data-testid="gallery-url-input"
              />
              <label className="flex items-center gap-2 bg-slate-100 text-slate-700 px-3 py-2 rounded-lg hover:bg-slate-200 cursor-pointer transition-colors">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                <span className="text-sm">Upload</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                  disabled={uploading}
                />
              </label>
            </div>
            <select
              value={formData.category}
              onChange={(e) => setFormData({...formData, category: e.target.value})}
              className="px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-secondary"
            >
              {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
            </select>
            <input
              type="number"
              placeholder="Sort Order"
              value={formData.sortOrder}
              onChange={(e) => setFormData({...formData, sortOrder: parseInt(e.target.value) || 0})}
              className="px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-secondary"
            />
          </div>
          <input
            type="text"
            placeholder="Caption"
            value={formData.caption}
            onChange={(e) => setFormData({...formData, caption: e.target.value})}
            className="w-full mt-4 px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-secondary"
          />
          {formData.imageUrl && (
            <div className="mt-4">
              <p className="text-sm text-slate-500 mb-2">Preview:</p>
              <img src={formData.imageUrl} alt="Preview" className="h-32 object-cover rounded-lg" />
            </div>
          )}
          <div className="flex gap-3 mt-4">
            <button onClick={handleSave} className="flex items-center gap-2 bg-secondary text-white px-4 py-2 rounded-lg hover:bg-secondary-600" data-testid="save-gallery-btn">
              <Save className="w-4 h-4" /> Save
            </button>
            <button onClick={() => { setShowForm(false); setEditingId(null); }} className="flex items-center gap-2 bg-slate-200 text-slate-700 px-4 py-2 rounded-lg hover:bg-slate-300">
              <X className="w-4 h-4" /> Cancel
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {data.map((item) => (
          <div key={item.id} className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden group">
            <div className="relative aspect-square">
              <img src={item.imageUrl} alt={item.title} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                <button onClick={() => handleEdit(item)} className="p-2 bg-white rounded-full text-slate-700 hover:text-secondary"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => handleDelete(item.id)} className="p-2 bg-white rounded-full text-slate-700 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
            <div className="p-3">
              <p className="font-medium text-sm text-primary-900 truncate">{item.title}</p>
              <p className="text-xs text-slate-500">{item.category}</p>
            </div>
          </div>
        ))}
      </div>
      {data.length === 0 && (
        <div className="text-center py-12 text-slate-500 bg-white rounded-xl">No images yet. Add your first image!</div>
      )}
    </div>
  );
};

// Documents Manager Component
const DocumentsManager = ({ authHeader }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ title: '', description: '', file: null });
  const [sortBy, setSortBy] = useState('uploadedAt');
  const [sortOrder, setSortOrder] = useState('desc');

  const fetchDocuments = useCallback(async () => {
    if (!authHeader?.Authorization) return;
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/documents`, {
        headers: authHeader
      });
      if (response.ok) {
        const data = await response.json();
        setDocuments(data);
      }
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to fetch documents:', err);
      }
    } finally {
      setLoading(false);
    }
  }, [authHeader]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const sortedDocuments = [...documents].sort((a, b) => {
    if (sortBy === 'title') {
      return sortOrder === 'asc' 
        ? a.title.localeCompare(b.title)
        : b.title.localeCompare(a.title);
    } else {
      return sortOrder === 'asc'
        ? new Date(a.uploadedAt) - new Date(b.uploadedAt)
        : new Date(b.uploadedAt) - new Date(a.uploadedAt);
    }
  });

  const handleUpload = async () => {
    if (!formData.file) {
      alert('Please select a file');
      return;
    }

    setUploading(true);
    try {
      const uploadData = new FormData();
      uploadData.append('file', formData.file);
      uploadData.append('title', formData.title || formData.file.name.replace(/\.[^/.]+$/, ''));
      uploadData.append('description', formData.description);

      const response = await fetch(`${API_URL}/api/admin/upload/document`, {
        method: 'POST',
        headers: { Authorization: authHeader.Authorization },
        body: uploadData
      });

      if (response.ok) {
        await fetchDocuments();
        setShowForm(false);
        setFormData({ title: '', description: '', file: null });
      } else {
        const error = await response.json();
        alert(error.detail || 'Upload failed');
      }
    } catch (err) {
      alert('Upload failed: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/documents/${id}`, {
        method: 'DELETE',
        headers: authHeader
      });
      if (response.ok) {
        await fetchDocuments();
      }
    } catch (err) {
      alert('Delete failed');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  };

  const getFileIcon = (fileType) => {
    if (fileType.includes('pdf')) return '📄';
    if (fileType.includes('word') || fileType.includes('document')) return '📝';
    if (fileType.includes('sheet') || fileType.includes('excel')) return '📊';
    if (fileType.includes('presentation') || fileType.includes('powerpoint')) return '📽️';
    return '📎';
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-secondary" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          <p className="text-slate-600">{documents.length} document{documents.length !== 1 ? 's' : ''}</p>
          <div className="flex items-center gap-2 text-sm">
            <span className="text-slate-500">Sort by:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-2 py-1 border border-slate-200 rounded text-sm"
            >
              <option value="uploadedAt">Date</option>
              <option value="title">Title</option>
            </select>
            <button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="px-2 py-1 border border-slate-200 rounded text-sm hover:bg-slate-50"
            >
              {sortOrder === 'asc' ? '↑' : '↓'}
            </button>
          </div>
        </div>
        <button
          onClick={() => { setShowForm(true); setFormData({ title: '', description: '', file: null }); }}
          className="flex items-center gap-2 bg-secondary text-white px-4 py-2 rounded-lg hover:bg-secondary-600 transition-colors"
          data-testid="add-document-btn"
        >
          <Upload className="w-5 h-5" />
          Upload Document
        </button>
      </div>

      {showForm && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
          <h3 className="font-heading text-lg font-semibold mb-4">Upload Document</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">File *</label>
              <input
                type="file"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                onChange={(e) => setFormData({...formData, file: e.target.files[0]})}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-secondary"
                data-testid="document-file-input"
              />
              <p className="text-xs text-slate-500 mt-1">Accepted: PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX (max 10MB)</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Title</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-secondary"
                placeholder="Document title (defaults to filename)"
                data-testid="document-title-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-secondary"
                rows={2}
                placeholder="Optional description"
              />
            </div>
          </div>
          <div className="flex gap-3 mt-4">
            <button
              onClick={handleUpload}
              disabled={uploading || !formData.file}
              className="flex items-center gap-2 bg-secondary text-white px-4 py-2 rounded-lg hover:bg-secondary-600 disabled:opacity-50"
              data-testid="upload-document-btn"
            >
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              Upload
            </button>
            <button
              onClick={() => setShowForm(false)}
              className="flex items-center gap-2 bg-slate-200 text-slate-700 px-4 py-2 rounded-lg hover:bg-slate-300"
            >
              <X className="w-4 h-4" />
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-6 py-3 text-sm font-semibold text-slate-600">Document</th>
              <th className="text-left px-6 py-3 text-sm font-semibold text-slate-600">Size</th>
              <th className="text-left px-6 py-3 text-sm font-semibold text-slate-600">Uploaded</th>
              <th className="text-right px-6 py-3 text-sm font-semibold text-slate-600">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {sortedDocuments.map((doc) => (
              <tr key={doc.id} className="hover:bg-slate-50">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{getFileIcon(doc.fileType)}</span>
                    <div>
                      <p className="font-medium text-primary-900">{doc.title}</p>
                      {doc.description && <p className="text-sm text-slate-500">{doc.description}</p>}
                      <p className="text-xs text-slate-400">{doc.originalFilename}</p>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 text-slate-600">{formatFileSize(doc.fileSize)}</td>
                <td className="px-6 py-4 text-slate-600">{formatDate(doc.uploadedAt)}</td>
                <td className="px-6 py-4 text-right">
                  <a 
                    href={`${API_URL}${doc.fileUrl}`} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="p-2 text-slate-500 hover:text-secondary inline-block"
                    title="Download"
                  >
                    <FileText className="w-4 h-4" />
                  </a>
                  <button 
                    onClick={() => handleDelete(doc.id)} 
                    className="p-2 text-slate-500 hover:text-red-500"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {documents.length === 0 && (
          <div className="text-center py-12 text-slate-500">No documents yet. Upload your first document!</div>
        )}
      </div>
    </div>
  );
};

// Images Manager Component
const ImagesManager = ({ authHeader }) => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ title: '', altText: '', category: 'General', file: null });
  const [filterCategory, setFilterCategory] = useState('');
  const [copiedId, setCopiedId] = useState(null);

  const categories = ['General', 'Worship', 'Community', 'Events', 'Building', 'People', 'Banners', 'Icons', 'Daycare and Preschool'];

  const fetchImages = useCallback(async () => {
    if (!authHeader?.Authorization) return;
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/images`, { headers: authHeader });
      if (response.ok) {
        const data = await response.json();
        setImages(data);
      }
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to fetch images:', err);
      }
    } finally {
      setLoading(false);
    }
  }, [authHeader]);

  useEffect(() => {
    fetchImages();
  }, [fetchImages]);

  const filteredImages = filterCategory 
    ? images.filter(img => img.category === filterCategory)
    : images;

  const handleUpload = async () => {
    if (!formData.file) {
      alert('Please select an image file');
      return;
    }

    setUploading(true);
    try {
      const uploadData = new FormData();
      uploadData.append('file', formData.file);
      uploadData.append('title', formData.title || formData.file.name.replace(/\.[^/.]+$/, ''));
      uploadData.append('altText', formData.altText || formData.title || formData.file.name.replace(/\.[^/.]+$/, ''));
      uploadData.append('category', formData.category);

      const response = await fetch(`${API_URL}/api/admin/images`, {
        method: 'POST',
        headers: { Authorization: authHeader.Authorization },
        body: uploadData
      });

      if (response.ok) {
        await fetchImages();
        setShowForm(false);
        setFormData({ title: '', altText: '', category: 'General', file: null });
      } else {
        const error = await response.json();
        alert(error.detail || 'Upload failed');
      }
    } catch (err) {
      alert('Upload failed: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/images/${id}`, {
        method: 'DELETE',
        headers: authHeader
      });
      if (response.ok) {
        await fetchImages();
      }
    } catch (err) {
      alert('Delete failed');
    }
  };

  const getImageUrl = (url) => {
    // If URL is already absolute (starts with http), use it directly
    if (url && (url.startsWith('http://') || url.startsWith('https://'))) {
      return url;
    }
    // Otherwise, prepend API_URL for local paths
    return `${API_URL}${url}`;
  };

  const copyToClipboard = async (imageUrl) => {
    const fullUrl = getImageUrl(imageUrl);
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopiedId(imageUrl);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = fullUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedId(imageUrl);
      setTimeout(() => setCopiedId(null), 2000);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-secondary" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          <p className="text-slate-600">{images.length} image{images.length !== 1 ? 's' : ''}</p>
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm"
          >
            <option value="">All Categories</option>
            {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
          </select>
        </div>
        <button
          onClick={() => { setShowForm(true); setFormData({ title: '', altText: '', category: 'General', file: null }); }}
          className="flex items-center gap-2 bg-secondary text-white px-4 py-2 rounded-lg hover:bg-secondary-600 transition-colors"
          data-testid="add-image-btn"
        >
          <Upload className="w-5 h-5" />
          Upload Image
        </button>
      </div>

      {/* Upload Form */}
      {showForm && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
          <h3 className="font-heading text-lg font-semibold mb-4">Upload Image</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-2">Image File *</label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setFormData({...formData, file: e.target.files[0]})}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-secondary"
                data-testid="image-file-input"
              />
              <p className="text-xs text-slate-500 mt-1">Accepted: JPEG, PNG, GIF, WebP (max 10MB)</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Title</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-secondary"
                placeholder="Image title"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Category</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({...formData, category: e.target.value})}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-secondary"
              >
                {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-2">Alt Text (for accessibility)</label>
              <input
                type="text"
                value={formData.altText}
                onChange={(e) => setFormData({...formData, altText: e.target.value})}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-secondary"
                placeholder="Describe the image for screen readers"
              />
            </div>
          </div>
          {formData.file && (
            <div className="mt-4">
              <p className="text-sm text-slate-500 mb-2">Preview:</p>
              <img 
                src={URL.createObjectURL(formData.file)} 
                alt="Preview" 
                className="h-32 object-cover rounded-lg"
              />
            </div>
          )}
          <div className="flex gap-3 mt-4">
            <button
              onClick={handleUpload}
              disabled={uploading || !formData.file}
              className="flex items-center gap-2 bg-secondary text-white px-4 py-2 rounded-lg hover:bg-secondary-600 disabled:opacity-50"
              data-testid="upload-image-btn"
            >
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              Upload
            </button>
            <button
              onClick={() => setShowForm(false)}
              className="flex items-center gap-2 bg-slate-200 text-slate-700 px-4 py-2 rounded-lg hover:bg-slate-300"
            >
              <X className="w-4 h-4" />
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Images Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
        {filteredImages.map((image) => (
          <div 
            key={image.id} 
            className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden group"
          >
            <div className="relative aspect-square">
              <img 
                src={getImageUrl(image.imageUrl || image.url)} 
                alt={image.altText || image.title} 
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                <button 
                  onClick={() => copyToClipboard(image.imageUrl || image.url)}
                  className="p-2 bg-white rounded-full text-slate-700 hover:text-secondary"
                  title="Copy URL"
                >
                  {copiedId === (image.imageUrl || image.url) ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                </button>
                <button 
                  onClick={() => handleDelete(image.id)} 
                  className="p-2 bg-white rounded-full text-slate-700 hover:text-red-500"
                  title="Delete"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="p-3">
              <p className="font-medium text-sm text-primary-900 truncate">{image.title}</p>
              <div className="flex justify-between items-center mt-1">
                <span className="text-xs text-slate-500">{image.category}</span>
                <span className="text-xs text-slate-400">{formatFileSize(image.fileSize)}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredImages.length === 0 && (
        <div className="text-center py-12 text-slate-500 bg-white rounded-xl">
          {filterCategory ? `No images in "${filterCategory}" category` : 'No images yet. Upload your first image!'}
        </div>
      )}

      {/* Usage Help */}
      <div className="mt-8 bg-slate-50 rounded-xl p-6">
        <h4 className="font-heading font-semibold text-primary-900 mb-2">Using Images in Pages</h4>
        <p className="text-sm text-slate-600 mb-3">
          Hover over any image and click the copy icon to copy the URL. Then paste it into:
        </p>
        <ul className="text-sm text-slate-600 list-disc list-inside space-y-1">
          <li>Page hero image fields</li>
          <li>Content page HTML: <code className="bg-white px-1 rounded">&lt;img src="URL" alt="description"&gt;</code></li>
          <li>Ministry or event image fields</li>
        </ul>
      </div>
    </div>
  );
};

// Pages Manager Component
const PagesManager = ({ authHeader }) => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPage, setEditingPage] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const fetchPages = useCallback(async () => {
    if (!authHeader?.Authorization) return;
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/pages`, { headers: authHeader });
      if (response.ok) {
        const data = await response.json();
        setPages(data);
      }
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to fetch pages:', err);
      }
    } finally {
      setLoading(false);
    }
  }, [authHeader]);

  useEffect(() => {
    fetchPages();
  }, [fetchPages]);

  const seedStaticPages = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/pages/seed-static`, {
        method: 'POST',
        headers: authHeader
      });
      if (response.ok) {
        await fetchPages();
        alert('Static pages seeded successfully!');
      }
    } catch (err) {
      alert('Failed to seed pages');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-secondary" />
      </div>
    );
  }

  const staticPages = pages.filter(p => p.pageType === 'static');
  const contentPages = pages.filter(p => p.pageType === 'content');

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <p className="text-slate-600">{staticPages.length} static pages, {contentPages.length} content pages</p>
        </div>
        <div className="flex gap-3">
          {staticPages.length === 0 && (
            <button
              onClick={seedStaticPages}
              className="flex items-center gap-2 bg-slate-200 text-slate-700 px-4 py-2 rounded-lg hover:bg-slate-300 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Initialize Static Pages
            </button>
          )}
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 bg-secondary text-white px-4 py-2 rounded-lg hover:bg-secondary-600 transition-colors"
            data-testid="create-content-page-btn"
          >
            <Plus className="w-5 h-5" />
            Create Content Page
          </button>
        </div>
      </div>

      {/* Static Pages */}
      {staticPages.length > 0 && (
        <div className="mb-8">
          <h3 className="font-heading text-lg font-semibold text-primary-900 mb-4">Static Pages</h3>
          <div className="grid gap-4">
            {staticPages.map((page) => (
              <div 
                key={page.slug}
                className="bg-white rounded-xl shadow-sm border border-slate-200 p-5 flex items-center justify-between"
              >
                <div>
                  <h4 className="font-semibold text-primary-900">{page.title}</h4>
                  <p className="text-sm text-slate-500">/{page.slug}</p>
                </div>
                <button
                  onClick={() => setEditingPage(page)}
                  className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors"
                >
                  <Pencil className="w-4 h-4" />
                  Edit
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Content Pages */}
      <div>
        <h3 className="font-heading text-lg font-semibold text-primary-900 mb-4">Content Pages</h3>
        {contentPages.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 text-center text-slate-500">
            No content pages yet. Create your first content page!
          </div>
        ) : (
          <div className="grid gap-4">
            {contentPages.map((page) => (
              <div 
                key={page.slug}
                className="bg-white rounded-xl shadow-sm border border-slate-200 p-5 flex items-center justify-between"
              >
                <div>
                  <h4 className="font-semibold text-primary-900">{page.title}</h4>
                  <p className="text-sm text-slate-500">/page/{page.slug}</p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setEditingPage(page)}
                    className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors"
                  >
                    <Pencil className="w-4 h-4" />
                    Edit
                  </button>
                  <button
                    onClick={async () => {
                      if (window.confirm('Delete this page?')) {
                        await fetch(`${API_URL}/api/admin/pages/${page.slug}`, {
                          method: 'DELETE',
                          headers: authHeader
                        });
                        fetchPages();
                      }
                    }}
                    className="p-2 text-slate-500 hover:text-red-500"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Edit Page Modal */}
      {editingPage && (
        <PageEditorModal 
          page={editingPage} 
          authHeader={authHeader} 
          onClose={() => setEditingPage(null)}
          onSave={() => {
            setEditingPage(null);
            fetchPages();
          }}
        />
      )}

      {/* Create Content Page Modal */}
      {showCreateModal && (
        <CreateContentPageModal 
          authHeader={authHeader} 
          onClose={() => setShowCreateModal(false)}
          onCreated={() => {
            setShowCreateModal(false);
            fetchPages();
          }}
        />
      )}
    </div>
  );
};

// Page Editor Modal
const PageEditorModal = ({ page, authHeader, onClose, onSave }) => {
  const [formData, setFormData] = useState(page);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('hero');

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/pages/${page.slug}`, {
        method: 'PUT',
        headers: {
          ...authHeader,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        alert('Page saved successfully!');
        onSave();
      } else {
        alert('Failed to save page');
      }
    } catch (err) {
      alert('Error saving page');
    } finally {
      setSaving(false);
    }
  };

  const updateHero = (field, value) => {
    setFormData({
      ...formData,
      hero: { ...formData.hero, [field]: value }
    });
  };

  const updateCta = (field, value) => {
    setFormData({
      ...formData,
      cta: { ...formData.cta, [field]: value }
    });
  };

  const updateSection = (sectionKey, value) => {
    setFormData({
      ...formData,
      sections: { ...formData.sections, [sectionKey]: value }
    });
  };

  const tabs = [
    { id: 'hero', label: 'Hero' },
    { id: 'content', label: 'Content' },
    ...(page.pageType === 'content' ? [{ id: 'gallery', label: 'Gallery' }] : []),
    { id: 'cta', label: 'Call to Action' },
    { id: 'seo', label: 'SEO' }
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <h2 className="font-heading text-xl font-semibold text-primary-900">Edit: {page.title}</h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-200 px-6">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-3 font-medium text-sm border-b-2 transition-colors ${
                activeTab === tab.id 
                  ? 'border-secondary text-secondary' 
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'hero' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Hero Image URL</label>
                <input
                  type="text"
                  value={formData.hero?.image || ''}
                  onChange={(e) => updateHero('image', e.target.value)}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-secondary"
                  placeholder="https://..."
                />
                {formData.hero?.image && (
                  <img src={formData.hero.image} alt="Preview" className="mt-2 h-32 object-cover rounded-lg" />
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Title</label>
                <input
                  type="text"
                  value={formData.hero?.title || ''}
                  onChange={(e) => updateHero('title', e.target.value)}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-secondary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Subtitle</label>
                <input
                  type="text"
                  value={formData.hero?.subtitle || ''}
                  onChange={(e) => updateHero('subtitle', e.target.value)}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-secondary"
                />
              </div>
            </div>
          )}

          {activeTab === 'content' && (
            <div className="space-y-4">
              {page.pageType === 'content' ? (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Page Content (HTML)</label>
                  <textarea
                    value={formData.content || ''}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-secondary font-mono text-sm"
                    rows={15}
                    placeholder="<p>Your content here...</p>"
                  />
                  <p className="text-xs text-slate-500 mt-1">Use HTML tags for formatting: &lt;p&gt;, &lt;h2&gt;, &lt;h3&gt;, &lt;ul&gt;, &lt;li&gt;, etc.</p>
                </div>
              ) : (
                <StaticPageContentEditor 
                  page={page} 
                  formData={formData} 
                  updateSection={updateSection}
                  setFormData={setFormData}
                />
              )}
            </div>
          )}

          {activeTab === 'gallery' && page.pageType === 'content' && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="showGallery"
                  checked={formData.gallerySection?.show ?? false}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    gallerySection: { ...formData.gallerySection, show: e.target.checked } 
                  })}
                  className="w-4 h-4 text-secondary"
                />
                <label htmlFor="showGallery" className="text-sm font-medium text-slate-700">Show Gallery Section at bottom of page</label>
              </div>
              {formData.gallerySection?.show && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Gallery Title</label>
                    <input
                      type="text"
                      value={formData.gallerySection?.title || ''}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        gallerySection: { ...formData.gallerySection, title: e.target.value } 
                      })}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-secondary"
                      placeholder="Photo Gallery"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Filter by Category</label>
                    <select
                      value={formData.gallerySection?.category || 'all'}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        gallerySection: { ...formData.gallerySection, category: e.target.value } 
                      })}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-secondary"
                    >
                      <option value="all">All Categories</option>
                      <option value="Worship">Worship</option>
                      <option value="Community">Community</option>
                      <option value="Youth">Youth</option>
                      <option value="Events">Events</option>
                      <option value="Building">Building</option>
                      <option value="Daycare and Preschool">Daycare and Preschool</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </>
              )}
              
              <div className="mt-6 p-4 bg-slate-50 rounded-lg">
                <h4 className="font-semibold text-primary-900 mb-2">Inline Gallery Shortcode</h4>
                <p className="text-sm text-slate-600 mb-3">
                  You can also embed a gallery anywhere in your page content using shortcodes:
                </p>
                <div className="bg-white rounded p-3 font-mono text-sm text-slate-700 space-y-1">
                  <p><code>[gallery:all]</code> - All photos</p>
                  <p><code>[gallery:worship]</code> - Worship photos</p>
                  <p><code>[gallery:community]</code> - Community photos</p>
                  <p><code>[gallery:youth]</code> - Youth photos</p>
                  <p><code>[gallery:daycare and preschool]</code> - Daycare photos</p>
                </div>
                <p className="text-xs text-slate-500 mt-2">
                  Add these codes to your Content tab HTML where you want the gallery to appear.
                </p>
              </div>
            </div>
          )}

          {activeTab === 'cta' && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="showCta"
                  checked={formData.cta?.show ?? true}
                  onChange={(e) => updateCta('show', e.target.checked)}
                  className="w-4 h-4 text-secondary"
                />
                <label htmlFor="showCta" className="text-sm font-medium text-slate-700">Show Call to Action section</label>
              </div>
              {formData.cta?.show && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Background Color</label>
                    <select
                      value={formData.cta?.bgColor || 'bg-primary-900'}
                      onChange={(e) => updateCta('bgColor', e.target.value)}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-secondary"
                    >
                      <option value="bg-primary-900">Dark Blue</option>
                      <option value="bg-slate-50">Light Gray</option>
                      <option value="bg-white">White</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Title</label>
                    <input
                      type="text"
                      value={formData.cta?.title || ''}
                      onChange={(e) => updateCta('title', e.target.value)}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-secondary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Description</label>
                    <textarea
                      value={formData.cta?.description || ''}
                      onChange={(e) => updateCta('description', e.target.value)}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-secondary"
                      rows={2}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Button Text</label>
                      <input
                        type="text"
                        value={formData.cta?.buttonText || ''}
                        onChange={(e) => updateCta('buttonText', e.target.value)}
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-secondary"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Button Link</label>
                      <input
                        type="text"
                        value={formData.cta?.buttonLink || ''}
                        onChange={(e) => updateCta('buttonLink', e.target.value)}
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-secondary"
                        placeholder="/contact"
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {activeTab === 'seo' && (
            <div className="space-y-4">
              <p className="text-sm text-slate-500 mb-4">
                Control how this page appears in search results and social media shares.
              </p>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Page Title
                  <span className="text-slate-400 font-normal ml-2">
                    ({(formData.seo?.title || '').length}/60 characters)
                  </span>
                </label>
                <input
                  type="text"
                  value={formData.seo?.title || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    seo: { ...formData.seo, title: e.target.value }
                  })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-secondary"
                  placeholder={`${formData.title || page.title || 'Page Title'} | Geneva United Methodist Church`}
                  maxLength={70}
                />
                <p className="text-xs text-slate-400 mt-1">
                  Leave blank to use: "{formData.title || page.title || 'Page Title'} | Geneva United Methodist Church"
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Meta Description
                  <span className="text-slate-400 font-normal ml-2">
                    ({(formData.seo?.description || '').length}/160 characters)
                  </span>
                </label>
                <textarea
                  value={formData.seo?.description || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    seo: { ...formData.seo, description: e.target.value }
                  })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-secondary"
                  rows={3}
                  placeholder="Enter a brief description of this page for search engines..."
                  maxLength={160}
                />
                <p className="text-xs text-slate-400 mt-1">
                  Recommended: 150-160 characters. This appears in Google search results.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Social Sharing Image URL
                </label>
                <input
                  type="text"
                  value={formData.seo?.image || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    seo: { ...formData.seo, image: e.target.value }
                  })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-secondary"
                  placeholder="https://..."
                />
                <p className="text-xs text-slate-400 mt-1">
                  Image shown when page is shared on Facebook, Twitter, LinkedIn. Recommended size: 1200x630px.
                </p>
              </div>

              <div className="mt-6 p-4 bg-slate-50 rounded-lg">
                <h4 className="font-semibold text-primary-900 mb-2">Search Preview</h4>
                <div className="bg-white rounded-lg p-4 border border-slate-200">
                  <p className="text-blue-600 text-lg truncate">
                    {formData.seo?.title || `${formData.title || page.title || 'Page Title'} | Geneva United Methodist Church`}
                  </p>
                  <p className="text-green-700 text-sm truncate">genevaumc.com/{page.slug}</p>
                  <p className="text-slate-600 text-sm line-clamp-2 mt-1">
                    {formData.seo?.description || 'No description set. Add a meta description to improve search results.'}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t border-slate-200">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-secondary text-white rounded-lg hover:bg-secondary-600 disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

// Static Page Content Editor (for complex page structures)
const StaticPageContentEditor = ({ page, formData, updateSection, setFormData }) => {
  const sections = formData.sections || {};

  // Link formatting tips component
  const LinkTips = () => (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
      <h5 className="font-semibold text-blue-800 text-sm mb-2">Adding Links</h5>
      <p className="text-blue-700 text-xs mb-2">Use HTML anchor tags to add links:</p>
      <ul className="text-blue-700 text-xs space-y-1">
        <li><code className="bg-blue-100 px-1 rounded">&lt;a href="/contact"&gt;Contact Us&lt;/a&gt;</code> - Internal link</li>
        <li><code className="bg-blue-100 px-1 rounded">&lt;a href="https://example.com" target="_blank"&gt;External Site&lt;/a&gt;</code> - External link</li>
      </ul>
    </div>
  );

  if (page.slug === 'history') {
    return (
      <div className="space-y-6">
        <LinkTips />
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Intro Text (HTML)</label>
          <textarea
            value={formData.content || ''}
            onChange={(e) => setFormData({ ...formData, content: e.target.value })}
            className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-secondary"
            rows={3}
          />
        </div>
        <div>
          <h4 className="font-semibold text-primary-900 mb-3">Timeline Items</h4>
          {(sections.timeline || []).map((item, index) => (
            <div key={`timeline-${index}`} className="bg-slate-50 rounded-lg p-4 mb-3">
              <input
                type="text"
                value={item.title}
                onChange={(e) => {
                  const newTimeline = [...(sections.timeline || [])];
                  newTimeline[index] = { ...item, title: e.target.value };
                  updateSection('timeline', newTimeline);
                }}
                className="w-full px-3 py-2 border border-slate-200 rounded mb-2 font-semibold"
                placeholder="Title"
              />
              <textarea
                value={item.content}
                onChange={(e) => {
                  const newTimeline = [...(sections.timeline || [])];
                  newTimeline[index] = { ...item, content: e.target.value };
                  updateSection('timeline', newTimeline);
                }}
                className="w-full px-3 py-2 border border-slate-200 rounded"
                rows={3}
                placeholder="Content"
              />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (page.slug === 'sunday-school') {
    const overview = sections.overview || {};
    return (
      <div className="space-y-6">
        <LinkTips />
        <div>
          <h4 className="font-semibold text-primary-900 mb-3">Overview Section</h4>
          <div className="grid gap-3">
            <input
              type="text"
              value={overview.heading || ''}
              onChange={(e) => updateSection('overview', { ...overview, heading: e.target.value })}
              className="w-full px-3 py-2 border border-slate-200 rounded"
              placeholder="Heading"
            />
            <textarea
              value={overview.description1 || ''}
              onChange={(e) => updateSection('overview', { ...overview, description1: e.target.value })}
              className="w-full px-3 py-2 border border-slate-200 rounded"
              rows={2}
              placeholder="First paragraph"
            />
            <textarea
              value={overview.description2 || ''}
              onChange={(e) => updateSection('overview', { ...overview, description2: e.target.value })}
              className="w-full px-3 py-2 border border-slate-200 rounded"
              rows={2}
              placeholder="Second paragraph"
            />
          </div>
        </div>
        <div>
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-semibold text-primary-900">Classes</h4>
            <button
              onClick={() => {
                const newClasses = [...(sections.classes || []), { title: 'New Class', description: '', time: '' }];
                updateSection('classes', newClasses);
              }}
              className="flex items-center gap-1 text-sm text-secondary hover:text-secondary-600"
            >
              <Plus className="w-4 h-4" />
              Add Class
            </button>
          </div>
          {(sections.classes || []).map((classItem, index) => (
            <div key={`class-${index}`} className="bg-slate-50 rounded-lg p-4 mb-3 relative">
              <button
                onClick={() => {
                  const newClasses = [...(sections.classes || [])];
                  newClasses.splice(index, 1);
                  updateSection('classes', newClasses);
                }}
                className="absolute top-2 right-2 p-1 text-slate-400 hover:text-red-500 transition-colors"
                title="Delete class"
              >
                <Trash2 className="w-4 h-4" />
              </button>
              <input
                type="text"
                value={classItem.title}
                onChange={(e) => {
                  const newClasses = [...(sections.classes || [])];
                  newClasses[index] = { ...classItem, title: e.target.value };
                  updateSection('classes', newClasses);
                }}
                className="w-full px-3 py-2 border border-slate-200 rounded mb-2 font-semibold"
                placeholder="Class Name"
              />
              <textarea
                value={classItem.description}
                onChange={(e) => {
                  const newClasses = [...(sections.classes || [])];
                  newClasses[index] = { ...classItem, description: e.target.value };
                  updateSection('classes', newClasses);
                }}
                className="w-full px-3 py-2 border border-slate-200 rounded mb-2"
                rows={2}
                placeholder="Description"
              />
              <input
                type="text"
                value={classItem.time}
                onChange={(e) => {
                  const newClasses = [...(sections.classes || [])];
                  newClasses[index] = { ...classItem, time: e.target.value };
                  updateSection('classes', newClasses);
                }}
                className="w-full px-3 py-2 border border-slate-200 rounded"
                placeholder="Time"
              />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (page.slug === 'pastoral-message') {
    const pastor = sections.pastor || {};
    return (
      <div className="space-y-6">
        <LinkTips />
        <div>
          <h4 className="font-semibold text-primary-900 mb-3">Pastor Info</h4>
          <div className="grid gap-3">
            <input
              type="text"
              value={pastor.image || ''}
              onChange={(e) => updateSection('pastor', { ...pastor, image: e.target.value })}
              className="w-full px-3 py-2 border border-slate-200 rounded"
              placeholder="Pastor Photo URL"
            />
            <input
              type="text"
              value={pastor.name || ''}
              onChange={(e) => updateSection('pastor', { ...pastor, name: e.target.value })}
              className="w-full px-3 py-2 border border-slate-200 rounded"
              placeholder="Pastor Name"
            />
            <input
              type="text"
              value={pastor.role || ''}
              onChange={(e) => updateSection('pastor', { ...pastor, role: e.target.value })}
              className="w-full px-3 py-2 border border-slate-200 rounded"
              placeholder="Role/Title"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Quote</label>
          <input
            type="text"
            value={sections.quote || ''}
            onChange={(e) => updateSection('quote', e.target.value)}
            className="w-full px-3 py-2 border border-slate-200 rounded"
            placeholder="Featured quote"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Message (HTML)</label>
          <textarea
            value={sections.message || ''}
            onChange={(e) => updateSection('message', e.target.value)}
            className="w-full px-3 py-2 border border-slate-200 rounded font-mono text-sm"
            rows={10}
            placeholder="<p>Dear Friends,</p><p>Your message...</p>"
          />
        </div>
      </div>
    );
  }

  // Generic fallback for other pages
  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-2">Page Sections (JSON)</label>
      <textarea
        value={JSON.stringify(sections, null, 2)}
        onChange={(e) => {
          try {
            const parsed = JSON.parse(e.target.value);
            setFormData({ ...formData, sections: parsed });
          } catch (err) {
            // Intentionally silent: Invalid JSON during typing is expected, don't update state
          }
        }}
        className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-secondary font-mono text-sm"
        rows={15}
      />
      <p className="text-xs text-slate-500 mt-1">Advanced: Edit the raw JSON structure for this page's sections.</p>
    </div>
  );
};

// Create Content Page Modal
const CreateContentPageModal = ({ authHeader, onClose, onCreated }) => {
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    content: '<p>Your content here...</p>',
    hero: { image: '', title: '', subtitle: '' },
    cta: { show: true, bgColor: 'bg-primary-900', title: '', description: '', buttonText: '', buttonLink: '' }
  });
  const [creating, setCreating] = useState(false);

  const handleCreate = async () => {
    if (!formData.title || !formData.slug) {
      alert('Title and URL slug are required');
      return;
    }

    setCreating(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/pages`, {
        method: 'POST',
        headers: {
          ...authHeader,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          pageType: 'content',
          hero: { ...formData.hero, title: formData.hero.title || formData.title }
        })
      });

      if (response.ok) {
        onCreated();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to create page');
      }
    } catch (err) {
      alert('Error creating page');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <h2 className="font-heading text-xl font-semibold text-primary-900">Create Content Page</h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Page Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-secondary"
              placeholder="My New Page"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">URL Slug *</label>
            <div className="flex items-center">
              <span className="text-slate-500 mr-1">/page/</span>
              <input
                type="text"
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })}
                className="flex-1 px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-secondary"
                placeholder="my-new-page"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Hero Image URL (optional)</label>
            <input
              type="text"
              value={formData.hero.image}
              onChange={(e) => setFormData({ ...formData, hero: { ...formData.hero, image: e.target.value } })}
              className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-secondary"
              placeholder="https://..."
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 p-6 border-t border-slate-200">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300"
          >
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={creating}
            className="flex items-center gap-2 px-4 py-2 bg-secondary text-white rounded-lg hover:bg-secondary-600 disabled:opacity-50"
          >
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Create Page
          </button>
        </div>
      </div>
    </div>
  );
};

// Homepage Manager Component
const HomepageManager = ({ authHeader }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [activeSection, setActiveSection] = useState('hero');

  const fetchSettings = useCallback(async () => {
    if (!authHeader?.Authorization) return;
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/homepage`, {
        headers: authHeader
      });
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      }
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to fetch homepage settings:', err);
      }
    } finally {
      setLoading(false);
    }
  }, [authHeader]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/homepage`, {
        method: 'PUT',
        headers: {
          ...authHeader,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(settings)
      });
      if (response.ok) {
        alert('Homepage settings saved successfully!');
      }
    } catch (err) {
      alert('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Are you sure you want to reset to default settings? This cannot be undone.')) return;
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/homepage/reset`, {
        method: 'POST',
        headers: authHeader
      });
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
        alert('Homepage reset to defaults');
      }
    } catch (err) {
      alert('Failed to reset settings');
    } finally {
      setSaving(false);
    }
  };

  const updateHero = (field, value) => {
    setSettings(prev => ({
      ...prev,
      hero: { ...prev.hero, [field]: value }
    }));
  };

  const updateWelcome = (field, value) => {
    setSettings(prev => ({
      ...prev,
      welcome: { ...prev.welcome, [field]: value }
    }));
  };

  const updateServiceTime = (index, field, value) => {
    setSettings(prev => {
      const newTimes = [...prev.hero.serviceTimes];
      newTimes[index] = { ...newTimes[index], [field]: value };
      return { ...prev, hero: { ...prev.hero, serviceTimes: newTimes } };
    });
  };

  const addServiceTime = () => {
    setSettings(prev => ({
      ...prev,
      hero: {
        ...prev.hero,
        serviceTimes: [...prev.hero.serviceTimes, { label: '', time: '', description: '' }]
      }
    }));
  };

  const removeServiceTime = (index) => {
    setSettings(prev => ({
      ...prev,
      hero: {
        ...prev.hero,
        serviceTimes: prev.hero.serviceTimes.filter((_, i) => i !== index)
      }
    }));
  };

  const updateHeroButton = (index, field, value) => {
    setSettings(prev => {
      const newButtons = [...prev.hero.buttons];
      newButtons[index] = { ...newButtons[index], [field]: value };
      return { ...prev, hero: { ...prev.hero, buttons: newButtons } };
    });
  };

  const updateWelcomeButton = (index, field, value) => {
    setSettings(prev => {
      const newButtons = [...prev.welcome.buttons];
      newButtons[index] = { ...newButtons[index], [field]: value };
      return { ...prev, welcome: { ...prev.welcome, buttons: newButtons } };
    });
  };

  const updateParagraph = (index, value) => {
    setSettings(prev => {
      const newParagraphs = [...prev.welcome.paragraphs];
      newParagraphs[index] = value;
      return { ...prev, welcome: { ...prev.welcome, paragraphs: newParagraphs } };
    });
  };

  const addParagraph = () => {
    setSettings(prev => ({
      ...prev,
      welcome: {
        ...prev.welcome,
        paragraphs: [...prev.welcome.paragraphs, '']
      }
    }));
  };

  const removeParagraph = (index) => {
    setSettings(prev => ({
      ...prev,
      welcome: {
        ...prev.welcome,
        paragraphs: prev.welcome.paragraphs.filter((_, i) => i !== index)
      }
    }));
  };

  // Feature card functions
  const updateFeatureCard = (index, field, value) => {
    setSettings(prev => {
      const newCards = [...(prev.featureCards || [])];
      newCards[index] = { ...newCards[index], [field]: value };
      return { ...prev, featureCards: newCards };
    });
  };

  const addFeatureCard = () => {
    setSettings(prev => ({
      ...prev,
      featureCards: [...(prev.featureCards || []), { title: '', description: '', link: '', icon: 'heart' }]
    }));
  };

  const removeFeatureCard = (index) => {
    setSettings(prev => ({
      ...prev,
      featureCards: (prev.featureCards || []).filter((_, i) => i !== index)
    }));
  };

  if (loading || !settings) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-secondary" />
      </div>
    );
  }

  return (
    <div>
      {/* Action Buttons */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex gap-2">
          <button
            onClick={() => setActiveSection('hero')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeSection === 'hero' ? 'bg-secondary text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            Hero Section
          </button>
          <button
            onClick={() => setActiveSection('welcome')}
            data-testid="homepage-section-welcome"
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeSection === 'welcome' ? 'bg-secondary text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            Welcome Section
          </button>
          <button
            onClick={() => setActiveSection('features')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeSection === 'features' ? 'bg-secondary text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            Feature Cards
          </button>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleReset}
            className="flex items-center gap-2 bg-slate-200 text-slate-700 px-4 py-2 rounded-lg hover:bg-slate-300"
          >
            <RefreshCw className="w-4 h-4" />
            Reset to Defaults
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 bg-secondary text-white px-4 py-2 rounded-lg hover:bg-secondary-600 disabled:opacity-50"
            data-testid="save-homepage-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Changes
          </button>
        </div>
      </div>

      {/* Hero Section Editor */}
      {activeSection === 'hero' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <h3 className="font-heading text-lg font-semibold mb-4">Hero Background & Text</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Background Image URL</label>
                <input
                  type="text"
                  value={settings.hero.backgroundImage}
                  onChange={(e) => updateHero('backgroundImage', e.target.value)}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-secondary"
                  placeholder="https://..."
                />
                {settings.hero.backgroundImage && (
                  <img src={settings.hero.backgroundImage} alt="Preview" className="mt-2 h-32 object-cover rounded-lg" />
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Tagline (small text above title)</label>
                <input
                  type="text"
                  value={settings.hero.tagline || ''}
                  onChange={(e) => updateHero('tagline', e.target.value)}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-secondary"
                  placeholder="Geneva United Methodist Church"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Main Title</label>
                <input
                  type="text"
                  value={settings.hero.title}
                  onChange={(e) => updateHero('title', e.target.value)}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-secondary"
                  placeholder="Open Hearts, Open Minds, Open Doors"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Subtitle</label>
                <input
                  type="text"
                  value={settings.hero.subtitle}
                  onChange={(e) => updateHero('subtitle', e.target.value)}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-secondary"
                  placeholder="We welcome all who seek to love the Lord"
                />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-heading text-lg font-semibold">Service Times</h3>
              <button
                onClick={addServiceTime}
                className="flex items-center gap-1 text-secondary text-sm font-medium hover:underline"
              >
                <Plus className="w-4 h-4" /> Add Time
              </button>
            </div>
            
            <div className="space-y-3">
              {settings.hero.serviceTimes.map((time, index) => (
                <div key={`service-time-${index}`} className="flex gap-3 items-start p-3 bg-slate-50 rounded-lg">
                  <div className="flex-1">
                    <input
                      type="text"
                      value={time.label}
                      onChange={(e) => updateServiceTime(index, 'label', e.target.value)}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm mb-2"
                      placeholder="Label (e.g., Sunday Worship)"
                    />
                    <input
                      type="text"
                      value={time.time}
                      onChange={(e) => updateServiceTime(index, 'time', e.target.value)}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                      placeholder="Time (e.g., 9:30 AM)"
                    />
                  </div>
                  <button
                    onClick={() => removeServiceTime(index)}
                    className="p-2 text-red-500 hover:bg-red-50 rounded"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <h3 className="font-heading text-lg font-semibold mb-4">Hero Buttons</h3>
            
            <div className="space-y-3">
              {settings.hero.buttons.map((btn, index) => (
                <div key={`hero-btn-${index}`} className="flex gap-3 items-center p-3 bg-slate-50 rounded-lg">
                  <input
                    type="text"
                    value={btn.text}
                    onChange={(e) => updateHeroButton(index, 'text', e.target.value)}
                    className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm"
                    placeholder="Button Text"
                  />
                  <input
                    type="text"
                    value={btn.link}
                    onChange={(e) => updateHeroButton(index, 'link', e.target.value)}
                    className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm"
                    placeholder="Link (e.g., /welcome)"
                  />
                  <select
                    value={btn.style}
                    onChange={(e) => updateHeroButton(index, 'style', e.target.value)}
                    className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
                  >
                    <option value="primary">Primary (Gold)</option>
                    <option value="outline">Outline</option>
                  </select>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Welcome Section Editor */}
      {activeSection === 'welcome' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <h3 className="font-heading text-lg font-semibold mb-4">Welcome Section Image</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Image URL</label>
                <input
                  type="text"
                  value={settings.welcome.image}
                  onChange={(e) => updateWelcome('image', e.target.value)}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-secondary"
                  placeholder="https://..."
                />
                {settings.welcome.image && (
                  <img src={settings.welcome.image} alt="Preview" className="mt-2 h-32 object-cover rounded-lg" />
                )}
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Badge Title</label>
                  <input
                    type="text"
                    value={settings.welcome.badgeTitle || ''}
                    onChange={(e) => updateWelcome('badgeTitle', e.target.value)}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-secondary"
                    placeholder="100+ Years"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Badge Subtitle</label>
                  <input
                    type="text"
                    value={settings.welcome.badgeSubtitle || ''}
                    onChange={(e) => updateWelcome('badgeSubtitle', e.target.value)}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-secondary"
                    placeholder="Serving Geneva"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <h3 className="font-heading text-lg font-semibold mb-4">Welcome Text</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Section Label (small text)</label>
                <input
                  type="text"
                  value={settings.welcome.sectionLabel || ''}
                  onChange={(e) => updateWelcome('sectionLabel', e.target.value)}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-secondary"
                  placeholder="Welcome to Our Church"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Title</label>
                  <input
                    type="text"
                    value={settings.welcome.title}
                    onChange={(e) => updateWelcome('title', e.target.value)}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-secondary"
                    placeholder="A Place Where"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Highlighted Text (gold)</label>
                  <input
                    type="text"
                    value={settings.welcome.highlightText || ''}
                    onChange={(e) => updateWelcome('highlightText', e.target.value)}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-secondary"
                    placeholder="Everyone Belongs"
                  />
                </div>
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-slate-700">Paragraphs (use [text](/link) for links)</label>
                  <button
                    onClick={addParagraph}
                    data-testid="add-welcome-paragraph-btn"
                    className="flex items-center gap-1 text-secondary text-sm font-medium hover:underline"
                  >
                    <Plus className="w-4 h-4" /> Add Paragraph
                  </button>
                </div>
                {settings.welcome.paragraphs.map((para, index) => (
                  <div key={`para-${index}`} className="flex gap-2 mb-2">
                    <textarea
                      value={para}
                      data-testid={`welcome-paragraph-input-${index}`}
                      onChange={(e) => updateParagraph(index, e.target.value)}
                      className="flex-1 px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-secondary"
                      rows={3}
                      placeholder="Enter paragraph text..."
                    />
                    <button
                      onClick={() => removeParagraph(index)}
                      data-testid={`remove-welcome-paragraph-${index}`}
                      className="p-2 text-red-500 hover:bg-red-50 rounded self-start"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <h3 className="font-heading text-lg font-semibold mb-4">Welcome Buttons</h3>
            
            <div className="space-y-3">
              {settings.welcome.buttons.map((btn, index) => (
                <div key={`welcome-btn-${index}`} className="flex gap-3 items-center p-3 bg-slate-50 rounded-lg">
                  <input
                    type="text"
                    value={btn.text}
                    onChange={(e) => updateWelcomeButton(index, 'text', e.target.value)}
                    className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm"
                    placeholder="Button Text"
                  />
                  <input
                    type="text"
                    value={btn.link}
                    onChange={(e) => updateWelcomeButton(index, 'link', e.target.value)}
                    className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm"
                    placeholder="Link (e.g., /about)"
                  />
                  <select
                    value={btn.style}
                    onChange={(e) => updateWelcomeButton(index, 'style', e.target.value)}
                    className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
                  >
                    <option value="primary">Primary (Gold)</option>
                    <option value="outline">Outline</option>
                  </select>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Feature Cards Section */}
      {activeSection === 'features' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h3 className="font-heading text-lg font-semibold">Feature Cards</h3>
                <p className="text-sm text-slate-500">These appear below the hero section. Each card can link to any page.</p>
              </div>
              <button
                onClick={addFeatureCard}
                className="flex items-center gap-2 text-secondary hover:text-secondary-600"
              >
                <Plus className="w-4 h-4" />
                Add Card
              </button>
            </div>

            <div className="space-y-4">
              {(settings.featureCards || []).map((card, index) => (
                <div key={`feature-card-${index}`} className="bg-slate-50 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-3">
                    <span className="text-sm font-medium text-slate-600">Card {index + 1}</span>
                    <button
                      onClick={() => removeFeatureCard(index)}
                      className="text-slate-400 hover:text-red-500"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="grid md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Title</label>
                      <input
                        type="text"
                        value={card.title}
                        onChange={(e) => updateFeatureCard(index, 'title', e.target.value)}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                        placeholder="Card Title"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Icon</label>
                      <select
                        value={card.icon || 'heart'}
                        onChange={(e) => updateFeatureCard(index, 'icon', e.target.value)}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                      >
                        <option value="heart">Heart</option>
                        <option value="users">Users/Community</option>
                        <option value="book">Book</option>
                        <option value="coffee">Coffee/Fellowship</option>
                        <option value="calendar">Calendar</option>
                        <option value="music">Music</option>
                        <option value="cross">Cross</option>
                        <option value="star">Star</option>
                        <option value="church">Church</option>
                      </select>
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-xs font-medium text-slate-600 mb-1">Description</label>
                      <input
                        type="text"
                        value={card.description}
                        onChange={(e) => updateFeatureCard(index, 'description', e.target.value)}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                        placeholder="Brief description"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-xs font-medium text-slate-600 mb-1">Link (optional)</label>
                      <input
                        type="text"
                        value={card.link || ''}
                        onChange={(e) => updateFeatureCard(index, 'link', e.target.value)}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                        placeholder="Internal: /welcome or External: https://example.com"
                      />
                      <p className="text-xs text-slate-400 mt-1">Leave blank for no link. Use /page/slug for content pages.</p>
                    </div>
                  </div>
                </div>
              ))}
              {(!settings.featureCards || settings.featureCards.length === 0) && (
                <div className="text-center py-8 text-slate-500">
                  No feature cards. Click "Add Card" to create one.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Contact Manager Component
const ContactManager = ({ authHeader }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);

  const fetchSettings = useCallback(async () => {
    if (!authHeader?.Authorization) return;
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/contact`, {
        headers: authHeader
      });
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      }
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to fetch contact settings:', err);
      }
    } finally {
      setLoading(false);
    }
  }, [authHeader]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/contact`, {
        method: 'PUT',
        headers: {
          ...authHeader,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(settings)
      });
      if (response.ok) {
        alert('Contact settings saved successfully!');
      }
    } catch (err) {
      alert('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Are you sure you want to reset to default settings?')) return;
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/contact/reset`, {
        method: 'POST',
        headers: authHeader
      });
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
        alert('Contact settings reset to defaults');
      }
    } catch (err) {
      alert('Failed to reset settings');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const updatePastor = (field, value) => {
    setSettings(prev => ({
      ...prev,
      pastor: { ...prev.pastor, [field]: value }
    }));
  };

  const updateAddress = (index, value) => {
    setSettings(prev => {
      const newAddress = [...prev.address];
      newAddress[index] = value;
      return { ...prev, address: newAddress };
    });
  };

  const addAddressLine = () => {
    setSettings(prev => ({
      ...prev,
      address: [...prev.address, '']
    }));
  };

  const removeAddressLine = (index) => {
    setSettings(prev => ({
      ...prev,
      address: prev.address.filter((_, i) => i !== index)
    }));
  };

  const updateOfficeHours = (index, field, value) => {
    setSettings(prev => {
      const newHours = [...prev.officeHours];
      newHours[index] = { ...newHours[index], [field]: value };
      return { ...prev, officeHours: newHours };
    });
  };

  const addOfficeHours = () => {
    setSettings(prev => ({
      ...prev,
      officeHours: [...prev.officeHours, { days: '', hours: '' }]
    }));
  };

  const removeOfficeHours = (index) => {
    setSettings(prev => ({
      ...prev,
      officeHours: prev.officeHours.filter((_, i) => i !== index)
    }));
  };

  if (loading || !settings) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-secondary" />
      </div>
    );
  }

  return (
    <div>
      {/* Action Buttons */}
      <div className="flex justify-end gap-2 mb-6">
        <button
          onClick={handleReset}
          className="flex items-center gap-2 bg-slate-200 text-slate-700 px-4 py-2 rounded-lg hover:bg-slate-300"
        >
          <RefreshCw className="w-4 h-4" />
          Reset to Defaults
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 bg-secondary text-white px-4 py-2 rounded-lg hover:bg-secondary-600 disabled:opacity-50"
          data-testid="save-contact-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save Changes
        </button>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Get In Touch Section */}
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <h3 className="font-heading text-lg font-semibold mb-4">Contact Information</h3>
            
            <div className="space-y-4">
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-slate-700">Address</label>
                  <button
                    onClick={addAddressLine}
                    className="flex items-center gap-1 text-secondary text-sm font-medium hover:underline"
                  >
                    <Plus className="w-4 h-4" /> Add Line
                  </button>
                </div>
                {settings.address.map((line, index) => (
                  <div key={`address-${index}`} className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={line}
                      onChange={(e) => updateAddress(index, e.target.value)}
                      className="flex-1 px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-secondary"
                      placeholder="Address line"
                    />
                    <button
                      onClick={() => removeAddressLine(index)}
                      className="p-2 text-red-500 hover:bg-red-50 rounded"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Phone</label>
                <input
                  type="text"
                  value={settings.phone}
                  onChange={(e) => updateField('phone', e.target.value)}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-secondary"
                  placeholder="(440) 466-2817"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Email</label>
                <input
                  type="email"
                  value={settings.email}
                  onChange={(e) => updateField('email', e.target.value)}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-secondary"
                  placeholder="genevaumchurch@windstream.net"
                />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-heading text-lg font-semibold">Office Hours</h3>
              <button
                onClick={addOfficeHours}
                className="flex items-center gap-1 text-secondary text-sm font-medium hover:underline"
              >
                <Plus className="w-4 h-4" /> Add Hours
              </button>
            </div>
            
            <div className="space-y-3">
              {settings.officeHours.map((hours, index) => (
                <div key={`office-hours-${index}`} className="flex gap-3 items-center p-3 bg-slate-50 rounded-lg">
                  <input
                    type="text"
                    value={hours.days}
                    onChange={(e) => updateOfficeHours(index, 'days', e.target.value)}
                    className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm"
                    placeholder="Days (e.g., Monday - Friday)"
                  />
                  <input
                    type="text"
                    value={hours.hours}
                    onChange={(e) => updateOfficeHours(index, 'hours', e.target.value)}
                    className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm"
                    placeholder="Hours (e.g., 9:00 AM - 3:00 PM)"
                  />
                  <button
                    onClick={() => removeOfficeHours(index)}
                    className="p-2 text-red-500 hover:bg-red-50 rounded"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Pastor & Other Section */}
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <h3 className="font-heading text-lg font-semibold mb-4">Pastor Information</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Name</label>
                <input
                  type="text"
                  value={settings.pastor.name}
                  onChange={(e) => updatePastor('name', e.target.value)}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-secondary"
                  placeholder="Pastor Pam Buzalka"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Title</label>
                <input
                  type="text"
                  value={settings.pastor.title || ''}
                  onChange={(e) => updatePastor('title', e.target.value)}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-secondary"
                  placeholder="Senior Pastor"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Description</label>
                <textarea
                  value={settings.pastor.description}
                  onChange={(e) => updatePastor('description', e.target.value)}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-secondary"
                  rows={3}
                  placeholder="Pastor Pam is available for appointments..."
                />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <h3 className="font-heading text-lg font-semibold mb-4">Additional Settings</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Google Maps Embed URL</label>
                <input
                  type="text"
                  value={settings.mapEmbedUrl || ''}
                  onChange={(e) => updateField('mapEmbedUrl', e.target.value)}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-secondary text-sm"
                  placeholder="https://www.google.com/maps/embed?..."
                />
                <p className="text-xs text-slate-500 mt-1">Paste the embed URL from Google Maps</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Accessibility Note</label>
                <textarea
                  value={settings.accessibilityNote || ''}
                  onChange={(e) => updateField('accessibilityNote', e.target.value)}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-secondary"
                  rows={2}
                  placeholder="Handicapped access and elevator available..."
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Users Manager Component
const UsersManager = ({ authHeader, currentUsername }) => {
  const { data, loading, createItem, updateItem, deleteItem, refetch } = useAdminData('users', authHeader);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    canManageUsers: false
  });
  const [error, setError] = useState('');
  const [initLoading, setInitLoading] = useState(false);

  const handleSave = async () => {
    setError('');
    
    if (!formData.username.trim()) {
      setError('Username is required');
      return;
    }
    
    if (!editingId && !formData.password.trim()) {
      setError('Password is required for new users');
      return;
    }

    let result;
    if (editingId) {
      // Only send password if it's been changed
      const updateData = {
        username: formData.username,
        canManageUsers: formData.canManageUsers
      };
      if (formData.password.trim()) {
        updateData.password = formData.password;
      }
      result = await updateItem(editingId, updateData);
    } else {
      result = await createItem(formData);
    }

    if (result.success) {
      setShowForm(false);
      setEditingId(null);
      setFormData({ username: '', password: '', canManageUsers: false });
    } else {
      setError(result.error || 'Failed to save user');
    }
  };

  const handleEdit = (user) => {
    setFormData({
      username: user.username,
      password: '',
      canManageUsers: user.canManageUsers || false
    });
    setEditingId(user.id);
    setShowForm(true);
    setError('');
  };

  const handleDelete = async (user) => {
    if (user.isSuperAdmin) {
      alert('Cannot delete super admin account');
      return;
    }
    if (user.username === currentUsername) {
      alert('Cannot delete your own account');
      return;
    }
    if (!window.confirm(`Are you sure you want to delete user "${user.username}"?`)) {
      return;
    }
    
    const result = await deleteItem(user.id);
    if (!result.success) {
      alert(result.error || 'Failed to delete user');
    }
  };

  const initSuperAdmin = async () => {
    setInitLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/users/init-super-admin`, {
        method: 'POST',
        headers: authHeader
      });
      if (response.ok) {
        alert('Super admin account created successfully!');
        refetch();
      } else {
        const data = await response.json();
        alert(data.detail || 'Failed to create super admin');
      }
    } catch (err) {
      alert('Failed to create super admin');
    } finally {
      setInitLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-secondary" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <p className="text-slate-600">{data.length} user{data.length !== 1 ? 's' : ''}</p>
          {data.length === 0 && (
            <p className="text-sm text-amber-600 mt-1">
              No users in database. Using environment variable credentials.
            </p>
          )}
        </div>
        <div className="flex gap-2">
          {data.length === 0 && (
            <button
              onClick={initSuperAdmin}
              disabled={initLoading}
              className="flex items-center gap-2 bg-amber-500 text-white px-4 py-2 rounded-lg hover:bg-amber-600 disabled:opacity-50"
            >
              {initLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Initialize Super Admin
            </button>
          )}
          <button
            onClick={() => { 
              setShowForm(true); 
              setEditingId(null); 
              setFormData({ username: '', password: '', canManageUsers: false }); 
              setError('');
            }}
            className="flex items-center gap-2 bg-secondary text-white px-4 py-2 rounded-lg hover:bg-secondary-600"
            data-testid="add-user-btn"
          >
            <Plus className="w-5 h-5" />
            Add User
          </button>
        </div>
      </div>

      {showForm && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
          <h3 className="font-heading text-lg font-semibold mb-4">
            {editingId ? 'Edit User' : 'New User'}
          </h3>
          
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-lg mb-4 text-sm">
              {error}
            </div>
          )}
          
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Username *</label>
              <input
                type="text"
                value={formData.username}
                onChange={(e) => setFormData({...formData, username: e.target.value})}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-secondary"
                placeholder="Enter username"
                data-testid="user-username-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Password {editingId ? '(leave blank to keep current)' : '*'}
              </label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-secondary"
                placeholder={editingId ? '••••••••' : 'Enter password'}
                data-testid="user-password-input"
              />
            </div>
          </div>
          
          <label className="flex items-center gap-3 mt-4 p-3 bg-slate-50 rounded-lg cursor-pointer">
            <input
              type="checkbox"
              checked={formData.canManageUsers}
              onChange={(e) => setFormData({...formData, canManageUsers: e.target.checked})}
              className="w-5 h-5 text-secondary rounded"
            />
            <div>
              <p className="font-medium text-slate-700">Can Manage Users</p>
              <p className="text-sm text-slate-500">Allow this user to add, edit, and delete other users</p>
            </div>
          </label>
          
          <div className="flex gap-3 mt-4">
            <button
              onClick={handleSave}
              className="flex items-center gap-2 bg-secondary text-white px-4 py-2 rounded-lg hover:bg-secondary-600"
              data-testid="save-user-btn"
            >
              <Save className="w-4 h-4" />
              Save
            </button>
            <button
              onClick={() => { setShowForm(false); setEditingId(null); setError(''); }}
              className="flex items-center gap-2 bg-slate-200 text-slate-700 px-4 py-2 rounded-lg hover:bg-slate-300"
            >
              <X className="w-4 h-4" />
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-6 py-3 text-sm font-semibold text-slate-600">Username</th>
              <th className="text-left px-6 py-3 text-sm font-semibold text-slate-600">Role</th>
              <th className="text-left px-6 py-3 text-sm font-semibold text-slate-600">Can Manage Users</th>
              <th className="text-right px-6 py-3 text-sm font-semibold text-slate-600">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data.map((user) => (
              <tr key={user.id} className="hover:bg-slate-50">
                <td className="px-6 py-4">
                  <span className="font-medium text-primary-900">{user.username}</span>
                  {user.username === currentUsername && (
                    <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">You</span>
                  )}
                </td>
                <td className="px-6 py-4">
                  {user.isSuperAdmin ? (
                    <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded font-medium">Super Admin</span>
                  ) : (
                    <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded">Admin</span>
                  )}
                </td>
                <td className="px-6 py-4">
                  {user.canManageUsers ? (
                    <span className="text-green-600 font-medium">Yes</span>
                  ) : (
                    <span className="text-slate-400">No</span>
                  )}
                </td>
                <td className="px-6 py-4 text-right">
                  <button 
                    onClick={() => handleEdit(user)} 
                    className="p-2 text-slate-500 hover:text-secondary"
                    title="Edit user"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  {!user.isSuperAdmin && user.username !== currentUsername && (
                    <button 
                      onClick={() => handleDelete(user)} 
                      className="p-2 text-slate-500 hover:text-red-500"
                      title="Delete user"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {data.length === 0 && (
          <div className="text-center py-12 text-slate-500">
            <p>No users in database yet.</p>
            <p className="text-sm mt-2">Click "Initialize Super Admin" to create the first user account.</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Backup Manager Component
const BackupManager = ({ authHeader }) => {
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [lastExport, setLastExport] = useState(null);

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/backup/export`, {
        headers: authHeader
      });
      
      if (response.ok) {
        const data = await response.json();
        
        // Create and download the file
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const date = new Date().toISOString().split('T')[0];
        a.download = `gumc-backup-${date}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        
        setLastExport({
          date: new Date().toLocaleString(),
          collections: Object.keys(data.collections).length,
          totalRecords: Object.values(data.collections).reduce((sum, arr) => sum + arr.length, 0)
        });
      } else {
        const error = await response.json();
        alert(error.detail || 'Export failed');
      }
    } catch (err) {
      alert('Export failed: ' + err.message);
    } finally {
      setExporting(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setShowConfirm(true);
    }
  };

  const handleImport = async () => {
    if (!selectedFile) return;
    
    setImporting(true);
    setShowConfirm(false);
    setImportResult(null);
    
    try {
      const fileContent = await selectedFile.text();
      const backupData = JSON.parse(fileContent);
      
      const response = await fetch(`${API_URL}/api/admin/backup/import`, {
        method: 'POST',
        headers: {
          ...authHeader,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(backupData)
      });
      
      if (response.ok) {
        const result = await response.json();
        setImportResult({
          success: true,
          message: result.message,
          results: result.results
        });
      } else {
        const error = await response.json();
        setImportResult({
          success: false,
          message: error.detail || 'Import failed'
        });
      }
    } catch (err) {
      setImportResult({
        success: false,
        message: 'Import failed: ' + err.message
      });
    } finally {
      setImporting(false);
      setSelectedFile(null);
    }
  };

  const cancelImport = () => {
    setShowConfirm(false);
    setSelectedFile(null);
  };

  return (
    <div>
      <div className="mb-6">
        <p className="text-slate-600">Export your database for backup or restore from a previous backup file.</p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Export Section */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-green-100 rounded-lg">
              <Download className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <h3 className="font-heading text-lg font-semibold text-primary-900">Export Database</h3>
              <p className="text-sm text-slate-500">Download all content as a JSON file</p>
            </div>
          </div>
          
          <div className="mb-4">
            <h4 className="text-sm font-medium text-slate-700 mb-2">What's included:</h4>
            <ul className="text-sm text-slate-600 space-y-1">
              <li>• Pages and content</li>
              <li>• Events and calendar</li>
              <li>• Ministries</li>
              <li>• Gallery images (metadata)</li>
              <li>• Navigation structure</li>
              <li>• Homepage settings</li>
              <li>• Contact form submissions</li>
            </ul>
          </div>
          
          <button
            onClick={handleExport}
            disabled={exporting}
            className="w-full flex items-center justify-center gap-2 bg-green-600 text-white px-4 py-3 rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
            data-testid="export-db-btn"
          >
            {exporting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Exporting...
              </>
            ) : (
              <>
                <Download className="w-5 h-5" />
                Export Database
              </>
            )}
          </button>
          
          {lastExport && (
            <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-green-700">
                <strong>Last export:</strong> {lastExport.date}
              </p>
              <p className="text-xs text-green-600 mt-1">
                {lastExport.collections} collections, {lastExport.totalRecords} total records
              </p>
            </div>
          )}
        </div>

        {/* Import Section */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-amber-100 rounded-lg">
              <UploadCloud className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <h3 className="font-heading text-lg font-semibold text-primary-900">Restore Database</h3>
              <p className="text-sm text-slate-500">Restore from a backup file</p>
            </div>
          </div>
          
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800">
                <strong>Warning:</strong> Restoring a backup will replace all current data. This action cannot be undone. Make sure to export a backup of your current data first.
              </div>
            </div>
          </div>
          
          <label className="w-full flex items-center justify-center gap-2 bg-amber-500 text-white px-4 py-3 rounded-lg hover:bg-amber-600 cursor-pointer transition-colors">
            <UploadCloud className="w-5 h-5" />
            Select Backup File
            <input
              type="file"
              accept=".json"
              onChange={handleFileSelect}
              className="hidden"
              disabled={importing}
              data-testid="import-file-input"
            />
          </label>
          
          {importing && (
            <div className="mt-4 flex items-center justify-center gap-2 text-amber-600">
              <Loader2 className="w-5 h-5 animate-spin" />
              Restoring database...
            </div>
          )}
          
          {importResult && (
            <div className={`mt-4 p-3 rounded-lg border ${
              importResult.success 
                ? 'bg-green-50 border-green-200' 
                : 'bg-red-50 border-red-200'
            }`}>
              <p className={`text-sm font-medium ${
                importResult.success ? 'text-green-700' : 'text-red-700'
              }`}>
                {importResult.message}
              </p>
              {importResult.success && importResult.results && (
                <div className="mt-2 text-xs text-green-600 space-y-1">
                  {Object.entries(importResult.results).map(([collection, result]) => (
                    <p key={collection}>
                      {collection}: {result.status} ({result.count || 0} records)
                    </p>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 overflow-hidden">
            <div className="p-6 border-b border-slate-200 bg-amber-50">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-full">
                  <AlertTriangle className="w-6 h-6 text-amber-600" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-primary-900">
                  Confirm Database Restore
                </h3>
              </div>
            </div>
            
            <div className="p-6">
              <p className="text-slate-700 mb-4">
                You are about to restore the database from:
              </p>
              <p className="font-mono text-sm bg-slate-100 px-3 py-2 rounded mb-4 break-all">
                {selectedFile?.name}
              </p>
              <p className="text-red-600 font-medium text-sm">
                This will permanently replace ALL current data in the database. This cannot be undone.
              </p>
            </div>
            
            <div className="p-4 bg-slate-50 border-t border-slate-200 flex gap-3 justify-end">
              <button
                onClick={cancelImport}
                className="px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleImport}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                data-testid="confirm-import-btn"
              >
                Yes, Restore Database
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Help Section */}
      <div className="mt-8 bg-slate-50 rounded-xl p-6">
        <h4 className="font-heading font-semibold text-primary-900 mb-3">Backup Tips</h4>
        <ul className="text-sm text-slate-600 space-y-2">
          <li>• <strong>Regular backups:</strong> Export your database before making major content changes</li>
          <li>• <strong>Safe storage:</strong> Keep backup files in a secure location (Google Drive, Dropbox, etc.)</li>
          <li>• <strong>File naming:</strong> Backups are automatically named with the date (e.g., gumc-backup-2025-01-15.json)</li>
          <li>• <strong>Images note:</strong> The backup includes image/document metadata and URLs, but not the actual files. Uploaded files are stored separately.</li>
          <li>• <strong>Before restoring:</strong> Always export a backup of your current data first, in case you need to revert</li>
        </ul>
      </div>
    </div>
  );
};

export default AdminDashboard;
